





import { formatWaveLabel, lineTypeForDegree, styleForDegree } from './ElliottDegreeStyles';
import {
  TRADE_HALF, splitConnector, dateIndex, themeFor, TRADE_HIT_WIDTH, decoratorRich, CARD_SIDE,
} from './tradeDecorator';
import { describeWave } from './elliottProjections';
import { yearlySummaryCards } from './yearlySummary';
import { findDateIndex } from './overlayUtils';
// Theme-aware ink for overlay labels. Read through the theme STORE rather than a prop, because
// this renderer is a pure function with no component to be passed one — the same reason
// `cycleHandleColors` reads it. See overlayInk.js for the contrast measurements.
import { overlayLabelInk, overlayLabelInkDim } from './overlayInk';
import { PENDING_COLOR, goldenCircleSize } from './pendingSignals';
import { resolveColor } from './workspaceColors';
import { octavesFor, bandSeries, planetaryLineBounds } from './planetaryLinePrice';
import {
  AUDIT_LINE_OPACITY, AUDIT_LINE_WIDTH, CHART_ANALYTICA_CLICK, CHART_ANALYTICA_DOWN,
  CHART_ANALYTICA_SCOPE_FILL, CHART_ANALYTICA_UP,
} from './analyticaColors';
import {
  VECTOR_WIDTH as TRADE_SIM_VECTOR_WIDTH,
  VECTOR_OPACITY as TRADE_SIM_VECTOR_OPACITY,
} from './tradeSimVectors';

const formatClassicAspectCell = (item, isDecl = false) => {
  if (!item || typeof item !== 'object') return null;

  if (isDecl) {
    const declValue = Number.isFinite(item.value) ? item.value.toFixed(2) : 'n/a';
    return `${item.pair || 'Planet'} decl ${declValue}°`;
  }

  const pair = item.pair || 'Pair';
  const aspect = item.aspect || 'Aspect';
  const orb = Number.isFinite(item.orbDeg) ? item.orbDeg.toFixed(2) : 'n/a';
  return `${pair} ${aspect} (orb ${orb}°)`;
};

const renderTwoColumnAspectRows = (items, isDecl = false) => {
  if (!Array.isArray(items) || items.length === 0) return '';
  const cells = items
    .map((item) => formatClassicAspectCell(item, isDecl))
    .filter(Boolean);
  if (cells.length === 0) return '';

  const rows = [];
  for (let i = 0; i < cells.length; i += 2) {
    const left = cells[i] || '';
    const right = cells[i + 1] || '';
    rows.push(
      `<div style="display:flex;gap:10px;margin:1px 0;">` +
      `<div style="flex:1;min-width:180px;">${left}</div>` +
      `<div style="flex:1;min-width:180px;">${right}</div>` +
      `</div>`
    );
  }
  return `<div style="margin-top:2px;">${rows.join('')}</div>`;
};







const getMarkings = (overlays, barData, options = {}) => {
  // Defensive: Filter out undefined/null overlays
  const filteredOverlays = Array.isArray(overlays) ? overlays.filter(o => o && typeof o === 'object' && o.type) : [];
  if (Array.isArray(overlays) && overlays.some(o => !o || typeof o !== 'object' || !o.type)) {
    console.warn('[getMarkings] WARNING: overlays array contained undefined, null, or malformed overlay(s):', overlays);
  }
  overlays = filteredOverlays;
  // --- BEGIN DIAGNOSTIC LOGGING ---
  console.log('[getMarkings] Initial overlays received:', overlays);
  if (!barData || !barData.dates) {
    console.warn('[getMarkings] barData missing or invalid:', barData);
  } else {
    console.log('[getMarkings] barData.dates:', barData.dates);
  }
  // --- END DIAGNOSTIC LOGGING ---
  const { dates } = barData;
  const markLines = [];
  const markAreas = [];
  const trendLines = [];
  const planetaryLineSeries = [];
  /**
   * THE TRADE CARDS, as content descriptors rather than as a series.
   *
   * <p>The chart component draws these as draggable `graphic` elements. They leave here as
   * data because the layout is in PIXELS and this module has no chart instance to ask --
   * see utils/tradeCardLayout.js.
   */
  const tradeCards = [];

  if (!Array.isArray(overlays) || overlays.length === 0) {
    // No overlays to render
    return { markLines, markAreas, trendLines, planetaryLineSeries, tradeCards };
  }

  // If overlays are present but no planetaryLine overlay is found, log a diagnostic
  const hasPlanetaryLine = overlays.some(ovr => (ovr.type || '').toLowerCase() === 'planetaryline');
  if (!hasPlanetaryLine) {
    console.warn('[getMarkings] No planetaryLine overlay present in overlays array.');
  }

  overlays.forEach((overlay, index) => {
    if (!overlay || !overlay.type) {
      console.warn(`[getMarkings] Skipping invalid overlay #${index}:`, overlay);
      return;
    }

    // Log overlay fields for debugging
    Object.keys(overlay).forEach(key => {
      if (overlay[key] === undefined) {
        console.warn(`[getMarkings] Overlay #${index} missing field:`, key);
      }
    });

    const type = overlay.type.toLowerCase();
    console.log(`[getMarkings] Processing overlay #${index} of type '${type}'`, overlay);

    if (type.startsWith('vline') || type.startsWith('verticalline')) {
      const dateIndex = findDateIndex(overlay.date, dates);
      console.log(`[getMarkings] vLine: date='${overlay.date}', found index=${dateIndex}`);
      if (dateIndex !== -1) {
        const markLine = {
          name: overlay.label || '',
          xAxis: dates[dateIndex],
          // lineType is honoured so a projected, not-yet-confirmed level can draw dashed while a
          // confirmed one stays solid. Defaults to solid, so every existing vLine is unaffected.
          lineStyle: { color: overlay.color || '#000000', width: overlay.thickness || 1, type: overlay.lineType || 'solid' },
          label: {
            show: true,
            position: 'insideStartTop',
            rotate: 90,
            distance: 15,
            formatter: (params) => {
              const [line1, line2] = params.name.split('\n');
              // If customLabelColor, prepend icon; else just label
              if (overlay.customLabelColor) {
                if (line2) {
                  return `{icon|}{line1|${line1}}\n{line2|${line2}}`;
                }
                return `{icon|}{line1|${line1}}`;
              } else {
                if (line2) {
                  return `{line1|${line1}}\n{line2|${line2}}`;
                }
                return `{line1|${line1}}`;
              }
            },
            rich: {
              icon: {
                width: 12,
                height: 12,
                borderRadius: 6, // circle; set to 2 for square
                backgroundColor: overlay.customLabelColor || 'transparent',
                borderColor: overlay.customLabelColor || 'transparent',
                borderWidth: 1,
                align: 'left',
                verticalAlign: 'middle',
                padding: [0, 4, 0, 0], // right space after icon
              },
              /*
               * ⚠ THEME-AWARE, and this was a real defect rather than a polish item.
               *
               * These were `#333` and `#666`, which against the dark canvas measure 1.50:1 and
               * 3.30:1. 3:1 is the floor at which a graphical object registers at all, so on the
               * dark theme the first line was not faint — it was absent, for EVERY vLine study in
               * the application. That is what made the Recent Pivot Fib tool look as though it had
               * not fired: it had, and it drew ten correctly-placed marks whose labels nobody
               * could read.
               *
               * Derived through `markOn(canvasFor(mode))`, so this adds no colour of its own and
               * cannot drift from the canvas constants — and the light theme keeps the ink it
               * always had, because `markOn` returns CYCLE_INK against a light canvas.
               */
              line1: {
                color: overlayLabelInk(),
                fontSize: 12,
                fontWeight: 'bold',
                align: 'left',
              },
              line2: {
                // Dimmed from the ink that works rather than reaching for a darker grey, which on
                // a dark canvas is the wrong direction entirely.
                color: overlayLabelInkDim(),
                fontSize: 10,
                align: 'left',
                padding: [4, 0, 0, 0],
              },
            },
          },
        };
        console.log('[getMarkings] Creating markLine:', markLine);
        markLines.push(markLine);
      } else {
        console.warn(`[getMarkings] Skipping vLine overlay due to invalid date index. Label: '${overlay.label}', Date: '${overlay.date}', Index: ${dateIndex}`);
        // Explicitly log the overlay that is not displayed
        window.skippedOverlays = window.skippedOverlays || [];
        window.skippedOverlays.push({ type: 'vLine', label: overlay.label, date: overlay.date, index: dateIndex });
      }
    } else if (type === 'areahighlight') {
      const startIndex = findDateIndex(overlay.date1, dates);
      const endIndex = findDateIndex(overlay.date2, dates);
      console.log('[OverlayDebug] getMarkings areaHighlight:', overlay.date1, overlay.date2, 'startIndex:', startIndex, 'endIndex:', endIndex, 'dates.length:', dates.length);
      console.log(`[getMarkings] areaHighlight: date1='${overlay.date1}', found startIndex=${startIndex}`);
      console.log(`[getMarkings] areaHighlight: date2='${overlay.date2}', found endIndex=${endIndex}`);
      if (startIndex !== -1 && endIndex !== -1) {
        /*
         * ⚠ THE LABEL IS DRAWN, AND THAT IS DELIBERATE — the caller decides by whether it sets one.
         *
         * ECharts draws a markArea's `name` as a label by default (`show: true, position: 'top'`),
         * so an overlay carrying a label gets one on the canvas and an overlay carrying `''` does
         * not. That is the contract both producers are written against:
         * `areaHighlightOverlays` labels the FIRST interval of a run and blanks the rest, so a
         * reader looking at the chart alone can see what the shading is without the words being
         * printed once per band.
         *
         * <p>⚠ IT WAS BRIEFLY `label: { show: false }` HERE, and that was the wrong place. It fixed
         * the smear by silencing EVERY area highlight — including the single intentional label the
         * other producer draws — which trades a defect for a capability. The smear was one caller
         * labelling all of its intervals; it is fixed in that caller. See
         * `addAreaHighlightOverlaysUtil`.
         */
        const markArea = [
          { name: overlay.label || '', xAxis: dates[startIndex],
            itemStyle: { color: roleColor(options, overlay.colorRole, overlay.color) } },
          { xAxis: dates[endIndex] },
        ];
        console.log('[getMarkings] Creating markArea:', markArea);
        markAreas.push(markArea);
      } else {
        console.warn(`[getMarkings] Skipping areaHighlight due to invalid date index. Label: '${overlay.label}', Start: '${overlay.date1}' -> ${startIndex}, End: '${overlay.date2}' -> ${endIndex}`);
        // Explicitly log the overlay/date pair that is not displayed
        window.skippedOverlays = window.skippedOverlays || [];
        window.skippedOverlays.push({ type: 'areaHighlight', label: overlay.label, date1: overlay.date1, date2: overlay.date2, startIndex, endIndex });
      }
    } else if (type === 'elliottwave') {
      /*
       * An Elliott count drawn as a polyline through its pivots, with each pivot labelled.
       *
       * Built as an array ALIGNED to the dates axis with '-' between pivots and connectNulls, not
       * as a list of [x, y] pairs. The price panel's x-axis is a category axis of date strings: a
       * pair-based series has to match a category exactly to land, and anything that does not match
       * is dropped silently — a polyline with invisible holes and no error anywhere. Aligning to
       * the axis by index removes the question.
       *
       * That is also why the backend sends barIndex alongside timestamp. The index addresses the
       * bar list the frontend itself supplied, so it resolves here without re-deriving anything.
       */
      const points = Array.isArray(overlay.points) ? overlay.points : [];
      if (points.length < 2) {
        console.warn('[getMarkings] Skipping elliottWave overlay with too few pivots:', overlay);
        return;
      }

      // One overlay, several drawings: a layer per wave degree, then the price targets, then the
      // forward projection. `layers` is the current shape; an overlay carrying only `points` is
      // drawn as a single layer so an older caller still renders.
      const layers = Array.isArray(overlay.layers) && overlay.layers.length > 0
        ? overlay.layers
        : [{ degree: overlay.degree, points }];

      // A chosen colour beats the degree's own pen. The degrees stay distinguishable by width, dash
      // and label notation, so the hierarchy survives the override.
      const overrideColor = typeof overlay.color === 'string' && overlay.color ? overlay.color : null;
      const penFor = (degree) => {
        const base = styleForDegree(degree);
        return overrideColor ? { ...base, color: overrideColor } : base;
      };

      /** Places a count's pivots on the axis by index, labelled in its degree's notation. */
      const alignLayer = (layer) => {
        const style = penFor(layer.degree);
        const aligned = new Array(dates.length).fill('-');
        let placed = 0;

        (layer.points || []).forEach(point => {
          const idx = Number(point.barIndex);
          if (!Number.isInteger(idx) || idx < 0 || idx >= dates.length) return;
          if (typeof point.price !== 'number' || !Number.isFinite(point.price)) return;

          const text = formatWaveLabel(point.label, layer.degree);
          // What this leg IS, carried on the data item so the series formatter can read it back.
          // Built here rather than in the formatter because the formatter runs on hover, when the
          // layer it belongs to is no longer in scope.
          const described = describeWave(point.label, layer.degree, layer.pattern);
          aligned[idx] = text
            ? {
                value: point.price,
                waveTooltip: described && [
                  `<b>Wave ${described.label}</b> &nbsp; <span style="opacity:.75">${text}</span>`,
                  `${described.degreeName} degree · ${described.kind}`,
                  described.guidelines.length
                    ? `<div style="margin-top:4px;opacity:.85">${
                      described.guidelines.map(g => `• ${g}`).join('<br/>')}</div>`
                    : '',
                ].filter(Boolean).join('<br/>'),
                label: {
                  show: true,
                  formatter: text,
                  // Above a peak, below a trough — using the pivot's own kind rather than guessing
                  // from neighbouring prices, which could disagree with the swing detector.
                  position: point.high ? 'top' : 'bottom',
                  color: '#fff',
                  backgroundColor: style.color,
                  borderRadius: 3,
                  padding: [2, 5],
                  // Per degree, not a constant. Colour alone does not read as hierarchy: with one
                  // size for everything, a count and its sub-count look like two rival counts.
                  fontSize: style.fontSize || 11,
                  fontWeight: 'bold',
                },
              }
            : point.price;
          placed++;
        });

        return { aligned, placed, style, degree: layer.degree, segments: layer.segments };
      };

      // Filtered AFTER mapping, so each entry must carry its own degree. Indexing back into
      // `layers` by position would read the wrong layer as soon as one is dropped for having too
      // few on-chart pivots — which is exactly when a coarse and a fine count disagree.
      const drawn = layers.map(alignLayer).filter(l => l.placed >= 2);
      if (drawn.length === 0) {
        console.warn('[getMarkings] elliottWave overlay resolved to fewer than 2 on-chart pivots');
        return;
      }

      drawn.forEach((layer, layerIndex) => {
        planetaryLineSeries.push({
          id: `__elliott__${overlay.id || index}__${layerIndex}`,
          type: 'line',
          name: layerIndex === 0
            ? (overlay.label || `Elliott ${layer.style.name}`)
            : `Elliott ${layer.style.name}`,
          data: layer.aligned,
          // The pivots are sparse by construction; without this the line would not join them.
          connectNulls: true,
          showSymbol: true,
          symbol: 'circle',
          // The finer the degree, the smaller the pivot dot, so a sub-count does not compete with
          // the structure containing it.
          symbolSize: Math.max(4, Math.round((layer.style.fontSize || 11) * 0.6)),
          itemStyle: { color: layer.style.color },
          lineStyle: {
            color: layer.style.color,
            width: layer.style.width,
            type: lineTypeForDegree(layer.degree),
          },
          emphasis: { disabled: true },
          // Coarser degrees underneath, so a fine count's dots are never hidden by a bold one.
          z: 120 + layerIndex,
          xAxisIndex: 0,
          yAxisIndex: 0,
          // A series-level formatter, which takes precedence over the chart's global one — that is
          // written for candlesticks and returns '' for everything else, which is why these pivots
          // had no tooltip at all. Only the labelled pivots carry text; the count's unlabelled
          // origin returns '' and shows nothing rather than an empty box.
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => (params?.data?.waveTooltip || ''),
          },
        });
      });

      // --- wave leg annotations -----------------------------------------------------------
      //
      // A scatter with invisible symbols, used purely as a text anchor. The alternative — labels on
      // the polyline's own data items — cannot work: those items are the PIVOTS, and a pivot
      // terminates one leg and begins the next, so a label there belongs to neither.
      //
      // The pivot chips still carry the notation ([3], (3), iii). These carry what it means, which
      // until now lived only in a tooltip and in the dialog.
      drawn.forEach((layer, layerIndex) => {
        // Off the ALIGNED layer, not layers[layerIndex]. `drawn` is filtered, so indexing back into
        // the unfiltered list reads the wrong layer the moment one is dropped for having too few
        // on-chart pivots — the same desync that once gave a surviving Minute count a Primary dash
        // pattern, and the reason alignLayer carries what it needs rather than an index.
        const segments = Array.isArray(layer.segments) ? layer.segments : [];
        if (segments.length === 0) return;

        const placed = segments.filter(seg =>
          Number.isInteger(seg.index) && seg.index >= 0 && seg.index < dates.length
          && typeof seg.price === 'number' && Number.isFinite(seg.price));
        if (placed.length === 0) return;

        planetaryLineSeries.push({
          id: `__elliott_annot__${overlay.id || index}__${layerIndex}`,
          type: 'scatter',
          name: `Elliott legs ${layer.style.name}`,
          data: placed.map((seg, segIndex) => ({
            value: [dates[seg.index], seg.price],
            // What this leg is, on hover. The label above is a chip that has to stay short; this is
            // where the reader finds out what the notation means, how far the leg actually ran, and
            // which guidelines apply to it.
            legTooltip: [
              `<b>Wave ${seg.label}</b> &nbsp;<span style="opacity:.75">${
                seg.degreeName || ''} · ${seg.kind || ''}</span>`,
              Number.isFinite(seg.bars) ? `${seg.bars} bars` : '',
              Number.isFinite(seg.priceMove)
                ? `${seg.priceMove >= 0 ? '+' : ''}${seg.priceMove.toFixed(2)}${
                  Number.isFinite(seg.pricePercent)
                    ? ` (${seg.priceMove >= 0 ? '+' : ''}${(seg.pricePercent * 100).toFixed(1)}%)`
                    : ''}`
                : '',
              Number.isFinite(seg.ratio) ? `${Math.abs(seg.ratio).toFixed(2)}× its reference wave` : '',
              (seg.guidelines && seg.guidelines.length)
                ? `<div style="margin-top:4px;opacity:.85">${
                  seg.guidelines.map(g => `• ${g}`).join('<br/>')}</div>`
                : '',
            ].filter(Boolean).join('<br/>'),
            // Above a rising leg, below a falling one: price between two pivots travels on the
            // inside of the chord joining them, so the outside is the side with room.
            label: {
              show: true,
              formatter: seg.text,
              position: seg.up ? 'top' : 'bottom',
              // Pixels, not price. A price-space offset would grow and shrink with the y-scale and
              // sit on the candles at some zooms; this clears the wicks at every zoom.
              //
              // Staggered on alternate legs. Zoomed out, consecutive midpoints are only a few
              // pixels apart horizontally, so same-height labels collide and the later one hides
              // the earlier — seen on live SPY, where the two Minute legs at the top of the count
              // overlapped almost completely. Two rows cannot fix every case, but they turn the
              // common one (a pair of adjacent legs) into something readable.
              distance: segIndex % 2 === 0 ? 10 : 30,
              color: '#fff',
              backgroundColor: layer.style.color,
              borderRadius: 3,
              padding: [2, 6],
              fontSize: Math.max(9, (layer.style.fontSize || 11) - 2),
              fontWeight: 'bold',
            },
          })),
          // The symbol is invisible but must still be HITTABLE, or the leg has no hover: a
          // symbolSize of 0 gives ECharts nothing to test the pointer against. So it is drawn at a
          // usable size in a fully transparent colour, which is a target without being a mark.
          symbol: 'circle',
          symbolSize: 18,
          itemStyle: { color: 'transparent', borderWidth: 0 },
          // NOT silent. It was, because nothing here used to be interactive; leaving it so would
          // swallow every pointer event on the leg and the tooltip below would never fire.
          silent: false,
          // Under the pivot chips, so where a short leg puts the two close together the chip wins.
          z: 117,
          xAxisIndex: 0,
          yAxisIndex: 0,
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => (params?.data?.legTooltip || ''),
          },
        });
      });

      // --- bridges across degree transitions --------------------------------------------------
      //
      // Dotted, thin, faint, unlabelled. The counts are independent readings at different
      // resolutions rather than a parent and its child, so the stretch between one count's last
      // pivot and the next one's first belongs to neither and must not be drawn as a leg. It is
      // still the same price series, though, and an unexplained break reads as the study having
      // dropped a segment — which is exactly how it was reported.
      (Array.isArray(overlay.bridges) ? overlay.bridges : []).forEach((bridge, bridgeIndex) => {
        const from = Number(bridge?.fromIndex);
        const to = Number(bridge?.toIndex);
        if (!Number.isInteger(from) || !Number.isInteger(to)) return;
        if (from < 0 || to >= dates.length || to <= from) return;

        planetaryLineSeries.push({
          id: `__elliott_bridge__${overlay.id || index}__${bridgeIndex}`,
          type: 'line',
          name: `Elliott unlabelled ${bridge.fromDegree} to ${bridge.toDegree}`,
          data: dates.map((_, i) => {
            if (i === from) return bridge.fromPrice;
            if (i === to) return bridge.toPrice;
            return '-';
          }),
          connectNulls: true,
          // No pivot dots: a dot here would read as a labelled turn, which is the one thing this
          // segment must not claim.
          showSymbol: false,
          lineStyle: {
            color: overrideColor || '#90a4ae',
            width: 1,
            type: 'dotted',
            opacity: 0.45,
          },
          emphasis: { disabled: true },
          // Beneath every count, so it never draws over a real leg.
          z: 115,
          xAxisIndex: 0,
          yAxisIndex: 0,
          tooltip: { show: false },
        });
      });

      // --- horizontal price targets -------------------------------------------------------
      //
      // Each runs from the pivot it is projected FROM, rightwards. Anchoring them at that pivot
      // rather than spanning the whole grid is what makes them readable as projections: a
      // full-width level says a price matters, but not which wave said so.
      // Targets are forward claims, so they take the projection pen rather than the degree's.
      // A user-chosen colour still wins, the same way it wins over the degree ladder.
      const projectionPen = overrideColor
        || (typeof overlay.projectionColor === 'string' && overlay.projectionColor)
        || '#f9a825';
      const targetStyle = { ...penFor(drawn[drawn.length - 1].degree), color: projectionPen };
      // How far a target runs. From the overlay so the builder's forward budget and the renderer's
      // cannot drift apart; the fallback matches ELLIOTT_PROJECTION_BARS for an overlay built
      // before the field existed.
      const projectionBars = Number.isInteger(overlay.projectionBars) ? overlay.projectionBars : 50;
      (Array.isArray(overlay.targets) ? overlay.targets : []).forEach((target, targetIndex) => {
        if (typeof target?.price !== 'number' || !Number.isFinite(target.price)) return;
        const from = Number(target.fromIndex);
        if (!Number.isInteger(from) || from < 0 || from >= dates.length) return;
        const to = Math.min(dates.length - 1, from + projectionBars);
        if (to <= from) return;

        planetaryLineSeries.push({
          id: `__elliott_target__${overlay.id || index}__${targetIndex}`,
          type: 'line',
          name: target.label || `Elliott target ${targetIndex + 1}`,
          data: dates.map((_, i) => (i === from || i === to ? target.price : '-')),
          connectNulls: true,
          showSymbol: false,
          lineStyle: { color: targetStyle.color, width: 1, type: 'dotted', opacity: 0.9 },
          emphasis: { disabled: true },
          z: 118,
          xAxisIndex: 0,
          yAxisIndex: 0,
          tooltip: { show: false },
          markPoint: {
            silent: true,
            symbol: 'circle',
            symbolSize: 0,
            label: {
              show: true,
              // The callout when the builder supplied one — "W3 Target 1.618×: 142.36" — falling
              // back to the terse label for an overlay built before callouts existed.
              formatter: target.callout || target.label || '',
              // 'left' anchors the label's RIGHT edge at the line's end, so it tracks the target
              // line's right edge and cannot cross onto the price scale — the same reason the
              // Fibonacci tool labels sit on that side.
              position: 'left',
              distance: 6,
              // A filled badge rather than bare text. These sit over empty future space where there
              // is nothing behind them, so bare text on a light chart and a dark one needs two
              // different colours; a badge needs one and reads on both.
              color: '#fff',
              backgroundColor: targetStyle.color,
              borderRadius: 3,
              padding: [3, 7],
              fontSize: 10,
              fontWeight: 'bold',
            },
            data: [{ coord: [dates[to], target.price] }],
          },
        });
      });

      // --- forward projection -------------------------------------------------------------
      //
      // Dashed, and one segment only. The engine has established where the count currently is;
      // sketching the waves after the one in progress would draw a shape nobody has evidence for,
      // in a pen that claims as much support as the part that is measured.
      // Superseded by the vectors below when there are any: that one segment is the nearest target
      // of the finest layer, which the vector set already contains and draws with its length and
      // its outcome. Two dashed lines from the same pivot to the same price is clutter, not
      // emphasis. Kept for an overlay built before `projections` existed.
      const projection = Array.isArray(overlay.projections) && overlay.projections.length > 0
        ? null : overlay.projection;
      if (projection
          && Number.isInteger(projection.from?.index) && Number.isInteger(projection.to?.index)
          && projection.from.index >= 0 && projection.from.index < dates.length) {
        const toIndex = Math.min(dates.length - 1, projection.to.index);
        if (toIndex > projection.from.index) {
          planetaryLineSeries.push({
            id: `__elliott_projection__${overlay.id || index}`,
            type: 'line',
            name: projection.label || 'Elliott projection',
            data: dates.map((_, i) => {
              if (i === projection.from.index) return projection.from.price;
              if (i === toIndex) return projection.to.price;
              return '-';
            }),
            connectNulls: true,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 5,
            itemStyle: { color: targetStyle.color },
            lineStyle: { color: targetStyle.color, width: 2, type: 'dashed', opacity: 0.85 },
            emphasis: { disabled: true },
            z: 119,
            xAxisIndex: 0,
            yAxisIndex: 0,
            tooltip: { show: false },
          });
        }
      }
      // --- projection vectors ---------------------------------------------------------------
      //
      // One dashed segment per standing projection: from the pivot it is measured off, to the price
      // it says price should reach, at the bar it says that should happen by. Direction and length
      // in both axes, rather than a horizontal level with a number on it.
      //
      // Coloured by OUTCOME, not by degree — green reached, red the count broke, amber out of time,
      // blue still open. Anchoring the study in the past is what makes an outcome knowable at all,
      // and putting it in the colour is what lets the chart answer "did any of this work" without
      // the panel open.
      (Array.isArray(overlay.projections) ? overlay.projections : []).forEach((vec, vecIndex) => {
        const from = Number(vec?.from?.index);
        const to = Number(vec?.to?.index);
        if (!Number.isInteger(from) || from < 0 || from >= dates.length) return;
        if (!Number.isInteger(to)) return;
        // Clamped to the chart rather than dropped: a projection running past the last painted bar
        // is still a projection, and drawing it to the edge says more than not drawing it.
        const end = Math.min(dates.length - 1, to);
        if (end <= from) return;

        const marks = [{ coord: [dates[end], vec.to.price] }];
        // Where price ACTUALLY reached it, which is generally not where the guideline said. The gap
        // between the two marks is how early or late the projection was.
        if (vec.reachedAt && Number.isInteger(vec.reachedAt.index)
            && vec.reachedAt.index >= 0 && vec.reachedAt.index < dates.length) {
          marks.push({
            coord: [dates[vec.reachedAt.index], vec.reachedAt.price],
            symbol: 'pin',
            symbolSize: 26,
            label: { show: true, formatter: 'hit', fontSize: 9, color: '#fff' },
            itemStyle: { color: vec.badgeColor || vec.color },
          });
        }

        planetaryLineSeries.push({
          id: `__elliott_vector__${overlay.id || index}__${vecIndex}`,
          type: 'line',
          name: `Elliott projection W${vec.wave} ${vec.ratio}x`,
          data: dates.map((_, i) => {
            if (i === from) return vec.from.price;
            if (i === end) return vec.to.price;
            return '-';
          }),
          connectNulls: true,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 5,
          itemStyle: { color: vec.color },
          lineStyle: { color: vec.color, width: 2, type: 'dashed', opacity: 0.9 },
          emphasis: { disabled: true },
          z: 121,
          xAxisIndex: 0,
          yAxisIndex: 0,
          tooltip: { show: false },
          markPoint: {
            silent: true,
            symbol: 'circle',
            symbolSize: 0,
            label: {
              show: true,
              formatter: vec.label || '',
              // Anchored on its right edge, so a vector ending near the grid's edge puts its label
              // inside the grid instead of over the price scale.
              position: 'left',
              distance: 8,
              align: 'left',
              lineHeight: 13,
              color: '#fff',
              // The BADGE carries the outcome's colour while the line stays gold. The line's job is
              // to be recognisable as a projection at a glance; the badge's is to say what became
              // of it, and it is the thing a reader actually looks at.
              backgroundColor: vec.badgeColor || vec.color,
              borderRadius: 3,
              padding: [4, 7],
              fontSize: 10,
              fontWeight: 'bold',
            },
            data: marks,
          },
        });
      });
    } else if (type === 'astrographline') {
      if (Array.isArray(overlay.data) && overlay.data.length > 0) {
        const pointMap = {};
        overlay.data.forEach(pt => {
          pointMap[(pt.date || '').substring(0, 10)] = {
            value: pt.value,
            longTerm: pt.longTerm,
            middleTerm: pt.middleTerm,
            decl: pt.decl,
            rawValue: pt.rawValue,
            longAspects: Array.isArray(pt.longAspects) ? pt.longAspects : null,
            middleAspects: Array.isArray(pt.middleAspects) ? pt.middleAspects : null,
            declAspects: Array.isArray(pt.declAspects) ? pt.declAspects : null,
          };
        });
        // Use simple numbers for chart rendering — extra props on objects can
        // confuse ECharts into treating them as data dimensions (causes spikes).
        // Tooltip data is stored in a closure-captured lookup by index.
        const tooltipLookup = {};
        const seriesData = barData.dates.map((date, idx) => {
          const point = pointMap[(date || '').substring(0, 10)];
          if (!point) return '-';             // ECharts 'no data' marker (null can be treated as 0)
          const v = point.value;
          if (typeof v !== 'number' || !isFinite(v)) return '-';
          tooltipLookup[idx] = point;        // for tooltip formatter
          return v;
        });
        // Diagnostic: comprehensive data quality analysis
        let matchCount = 0, nullCount = 0, minVal = Infinity, maxVal = -Infinity;
        const numericVals = [];
        const nonNumericEntries = [];
        for (let i = 0; i < seriesData.length; i++) {
          const d = seriesData[i];
          if (typeof d === 'number' && isFinite(d)) {
            matchCount++;
            if (d < minVal) minVal = d;
            if (d > maxVal) maxVal = d;
            numericVals.push({ idx: i, val: d, date: (barData.dates[i] || '').substring(0, 10) });
          } else {
            nullCount++;
            if (d !== '-' && d !== null && d !== undefined) {
              nonNumericEntries.push({ idx: i, val: d, type: typeof d, date: (barData.dates[i] || '').substring(0, 10) });
            }
          }
        }
        // Find outliers: sort by absolute value, show top 10 extremes
        const sorted = [...numericVals].sort((a, b) => Math.abs(b.val) - Math.abs(a.val));
        const top10 = sorted.slice(0, 10);
        // Compute mean and std dev
        const mean = numericVals.reduce((s, e) => s + e.val, 0) / (numericVals.length || 1);
        const variance = numericVals.reduce((s, e) => s + (e.val - mean) ** 2, 0) / (numericVals.length || 1);
        const stdDev = Math.sqrt(variance);
        // Values beyond 3 std deviations
        const outliers = numericVals.filter(e => Math.abs(e.val - mean) > 3 * stdDev);
        console.log(`[AstroGraph DATA] total=${seriesData.length}, matched=${matchCount}, nulls=${nullCount}, range=[${minVal},${maxVal}], mean=${mean.toFixed(2)}, stdDev=${stdDev.toFixed(2)}`);
        console.log(`[AstroGraph DATA] top10 extreme values:`, top10);
        console.log(`[AstroGraph DATA] outliers (>3σ from mean): ${outliers.length}`, outliers.slice(0, 20));
        if (nonNumericEntries.length > 0) console.warn(`[AstroGraph DATA] non-numeric entries:`, nonNumericEntries.slice(0, 10));
        console.log(`[AstroGraph DATA] sample first 5:`, seriesData.slice(0, 5), `last 5:`, seriesData.slice(-5));
        const label = overlay.label || 'TrendPulse';
        const lineColor = overlay.color || '#ff6600';
        const lineSeries = {
          // Unique per overlay. Several TrendPulse curves can share a chart now, and a fixed id
          // would make ECharts treat them as one series — the last one drawn would replace the
          // rest, reproducing by another route exactly the overwrite this feature removed.
          id: `__astrograph__${overlay.id ? `_${overlay.id}` : `_${index}`}`,
          type: 'line',
          name: label,
          data: seriesData,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: true,
          triggerLineEvent: true,
          lineStyle: {
            color: lineColor,
            width: overlay.thickness || 1.5,
            type: overlay.lineType || 'solid',
            opacity: 0.7,
          },
          itemStyle: { color: '#000000' },
          z: 110,
          clip: true,
          xAxisIndex: 0,
          yAxisIndex: 6,
          connectNulls: false,
          endLabel: { show: true, formatter: label, color: 'inherit', offset: [0, -2], fontSize: 12, fontWeight: 'bold' },
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => {
              const data = tooltipLookup[params.dataIndex] || null;
              const longTerm = data && Number.isFinite(data.longTerm) ? data.longTerm : null;
              const middleTerm = data && Number.isFinite(data.middleTerm) ? data.middleTerm : null;
              const decl = data && Number.isFinite(data.decl) ? data.decl : null;
              const longAspects = data && Array.isArray(data.longAspects) ? data.longAspects : [];
              const middleAspects = data && Array.isArray(data.middleAspects) ? data.middleAspects : [];
              const declAspects = data && Array.isArray(data.declAspects) ? data.declAspects : [];

              const lines = [
                `<b>${label}</b>`,
                `${params.axisValueLabel || params.name}`,
              ];

              if (longTerm !== null && middleTerm !== null && decl !== null) {
                if (longAspects.length > 0) {
                  lines.push(`<span style="font-weight:600;">Long-term aspects (${longAspects.length})</span>`);
                  lines.push(renderTwoColumnAspectRows(longAspects));
                }

                if (middleAspects.length > 0) {
                  lines.push(`<span style="font-weight:600;">Middle-term aspects (${middleAspects.length})</span>`);
                  lines.push(renderTwoColumnAspectRows(middleAspects));
                }

                if (declAspects.length > 0) {
                  lines.push(`<span style="font-weight:600;">Declination inputs</span>`);
                  lines.push(renderTwoColumnAspectRows(declAspects, true));
                }
              }

              return lines.join('<br/>');
            }
          }
        };
        console.log('[getMarkings] Creating astroGraphLine series', lineSeries);
        planetaryLineSeries.push(lineSeries);
      } else {
        console.warn('[getMarkings] Skipping astroGraphLine with empty or missing data:', overlay);
      }
    } else if (type === 'trendline') {
      if (overlay.price1 !== undefined && overlay.price2 !== undefined) {
        console.log('[getMarkings] Creating trendLine:', overlay);
        trendLines.push(overlay);
      } else {
        console.warn(`[getMarkings] Skipping trendline with missing price data. Label: '${overlay.label}', price1: ${overlay.price1}, price2: ${overlay.price2}`, overlay);
        // Explicitly log the overlay that is not displayed
        window.skippedOverlays = window.skippedOverlays || [];
        window.skippedOverlays.push({ type: 'trendLine', label: overlay.label, price1: overlay.price1, price2: overlay.price2 });
      }
    } else if (type === 'planetaryline') {
      // Validate data length matches barData.dates
      if (Array.isArray(overlay.data) && overlay.data.length === barData.dates.length) {
        // Bind each longitude value to its corresponding date for ECharts time/category x-axis
        // Normalize overlay.data to a flat array of longitude numbers
        let planetaryData = overlay.data;
        if (Array.isArray(planetaryData) && planetaryData.length > 0 && typeof planetaryData[0] === 'object' && 'value' in planetaryData[0]) {
           // Convert [{value: [date, longitude]}, ...] to [longitude, ...]
           planetaryData = planetaryData.map(pt => Array.isArray(pt.value) ? pt.value[1] : pt.value);
           
           const pricePer360 = parseFloat(overlay.pricePer360) || 200;

           if (overlay.panel === 'main') {
             if (overlay.mode === 'declination') {
               const hasDeclinationBands = planetaryLineSeries.some(series => series && series.id === 'declination-fixed-bands');
               if (!hasDeclinationBands) {
                 const bandStyle = {
                   color: 'rgba(64, 140, 255, 0.14)'
                 };
                 const boundaryLineStyle = {
                   color: 'rgba(64, 140, 255, 0.70)',
                   width: 1,
                   type: 'dashed'
                 };
                 planetaryLineSeries.push({
                   id: 'declination-fixed-bands',
                   type: 'line',
                   name: 'Declination Bands',
                   data: [],
                   symbol: 'none',
                   silent: true,
                   xAxisIndex: 0,
                   yAxisIndex: 5,
                   z: 10,
                   lineStyle: {
                     opacity: 0
                   },
                   markArea: {
                     silent: true,
                     itemStyle: bandStyle,
                     data: [
                       [
                         { yAxis: -1 },
                         { yAxis: 1 }
                       ],
                       [
                         { yAxis: 22.5 },
                         { yAxis: 23.5 }
                       ],
                       [
                         { yAxis: -23.5 },
                         { yAxis: -22.5 }
                       ]
                     ]
                   },
                   markLine: {
                     silent: true,
                     symbol: 'none',
                     label: { show: false },
                     data: [
                       { yAxis: -1, lineStyle: boundaryLineStyle },
                       { yAxis: 0, lineStyle: { color: 'rgba(32, 100, 220, 0.95)', width: 1.5, type: 'solid' } },
                       { yAxis: 1, lineStyle: boundaryLineStyle },
                       { yAxis: 22.5, lineStyle: boundaryLineStyle },
                       { yAxis: 23.5, lineStyle: boundaryLineStyle },
                       { yAxis: -23.5, lineStyle: boundaryLineStyle },
                       { yAxis: -22.5, lineStyle: boundaryLineStyle }
                     ]
                   }
                 });
               }
               // --- Single line for Declination (Main Panel) ---
               const declinationData = barData.dates.map((date, i) => [date, planetaryData[i]]);
               const declinationLine = {
                   type: 'line',
                   name: `${overlay.planet || 'Declination'}`,
                   data: declinationData,
                   symbol: 'none',
                   lineStyle: {
                       color: overlay.color || '#00AAFF',
                       width: overlay.thickness === 'thick' ? 3 : overlay.thickness === 'medium' ? 2 : 1,
                       type: overlay.lineType || 'solid',
                   },
                   z: 100,
                   xAxisIndex: 0,
                   yAxisIndex: 5, // Use the dedicated Declination axis (Auto-scaled)
                   endLabel: {
                     show: true,
                     formatter: '{a}',
                     color: 'inherit',
                     offset: [0, -2],
                     fontSize: 14,
                     fontWeight: 'bold'
                   },
                   markPoint: {
                     symbol: 'pin',
                     symbolSize: 50,
                     label: {
                        show: true,
                        formatter: overlay.planet || 'Declination',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold'
                     },
                     itemStyle: { color: overlay.color || '#00AAFF' },
                     data: [
                       {
                         coord: declinationData[declinationData.length - 1],
                         value: overlay.planet || 'Declination'
                       }
                     ]
                   },
                   emphasis: {
                     focus: 'series',
                     label: {
                       show: true,
                       formatter: '{a}',
                       position: 'top',
                       fontSize: 12,
                       fontWeight: 'bold',
                       color: 'inherit'
                     }
                   },
                   tooltip: {
                     show: true,
                     trigger: 'item',
                     formatter: (params) => {
                       return `${params.seriesName}: ${params.value[1].toFixed(2)}°`;
                     }
                   }
               };
               console.log('[getMarkings] Creating Declination line', declinationLine);
               planetaryLineSeries.push(declinationLine);

             } else {
               // --- Repeated planetary lines for main panel (Longitude) ---
               //
               // planetaryData holds PRICES, not degrees: App.js already unwrapped the longitude
               // series and scaled it by pricePer360/360. The mapping here used to be
               // `(value % 360) * pricePer360 / 360`, which scaled a second time and took a
               // modulus at 360 price units — so for Venus at pricePer360=200 the band repeated
               // every 405 days against a 225-day orbit, and reset at 288 degrees rather than at
               // Aries. Reducing modulo pricePer360 makes one band exactly one revolution again.
               const { low: minPrice, high: maxPrice } = planetaryLineBounds(barData);
               const octaves = octavesFor(overlay, minPrice, maxPrice, pricePer360);
               const numReps = octaves.length;
               for (let rep = 0; rep < numReps; ++rep) {
                   const k = octaves[rep];
                   // bandSeries inserts a null at each revolution boundary so ECharts starts a new
                   // path instead of drawing a full-height vertical line back down the chart.
                   const seriesData = bandSeries(barData.dates, planetaryData, pricePer360, minPrice, k);
                   const planetaryLine = {
                   type: 'line',
                   name: `${overlay.planet || 'Planetary Line'}${numReps > 1 ? ' (N=' + k + ')' : ''}`,
                   data: seriesData,
                   symbol: 'none',
                   lineStyle: {
                       color: overlay.color || '#00AAFF',
                       width: overlay.thickness === 'thick' ? 3 : overlay.thickness === 'medium' ? 2 : 1,
                       type: overlay.lineType || 'solid',
                   },
                   z: 100,
                   xAxisIndex: 0,
                   yAxisIndex: 0,
                   endLabel: {
                     show: true,
                     formatter: '{a}',
                     color: 'inherit',
                     offset: [0, -2],
                     fontSize: 14,
                     fontWeight: 'bold'
                   },
                   markPoint: {
                     symbol: 'pin',
                     symbolSize: 50,
                     label: {
                        show: true,
                        formatter: overlay.planet || 'Planet',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold'
                     },
                     itemStyle: { color: overlay.color || '#00AAFF' },
                     data: [
                       {
                         coord: [...seriesData].reverse().find(pt => pt && pt[1] !== null) || seriesData[seriesData.length - 1],
                         value: overlay.planet || 'Planet'
                       }
                     ]
                   },
                   emphasis: {
                     focus: 'series',
                     label: {
                       show: true,
                       formatter: '{a}',
                       position: 'top',
                       fontSize: 12,
                       fontWeight: 'bold',
                       color: 'inherit'
                     }
                   },
                   tooltip: {
                     show: true,
                     trigger: 'item',
                     formatter: (params) => {
                       return `${overlay.planet || 'Planet'}: ${params.value[1].toFixed(2)}`;
                     }
                   }
                   };
                   console.log('[getMarkings] Creating planetaryLine repetition', k+1, 'of', numReps, planetaryLine);
                   planetaryLineSeries.push(planetaryLine);
               }
             }
           } else if (overlay.panel === 'volume') {
             // Map longitude 0->360 for planetary lines in volume panel using Dedicated Axis (Index 4)
             const volumeLineData = barData.dates.map((date, i) => [date, planetaryData[i]]);
             const planetaryLine = {
               type: 'line',
               name: overlay.planet || 'Planetary Line',
               data: volumeLineData,
               symbol: 'none',
               lineStyle: {
                 color: overlay.color || '#00AAFF',
                 width: overlay.thickness === 'thick' ? 3 : overlay.thickness === 'medium' ? 2 : 1,
                 type: overlay.lineType || 'solid',
               },
               z: 100,
               xAxisIndex: 1,
               yAxisIndex: 4, // Use dedicated Longitude axis
               endLabel: {
                 show: true,
                 formatter: '{a}',
                 color: 'inherit',
                 offset: [0, -2],
                 fontSize: 14,
                 fontWeight: 'bold'
               },
               markPoint: {
                 symbol: 'pin',
                 symbolSize: 50,
                 label: {
                    show: true,
                    formatter: overlay.planet || 'Planet',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 'bold'
                 },
                 itemStyle: { color: overlay.color || '#00AAFF' },
                 data: [
                   {
                     coord: volumeLineData[volumeLineData.length - 1],
                     value: overlay.planet || 'Planet'
                   }
                 ]
               },
               emphasis: {
                 focus: 'series',
                 label: {
                   show: true,
                   formatter: '{a}',
                   position: 'top',
                   fontSize: 12,
                   fontWeight: 'bold',
                   color: 'inherit'
                 }
               },
               tooltip: {
                 show: true,
                 trigger: 'item',
                 formatter: (params) => {
                   return `${overlay.planet || 'Planet'}: ${params.value[1].toFixed(2)}°`;
                 }
               }
             };
             planetaryLineSeries.push(planetaryLine);
           }
        }
      } else {
        console.warn(`[getMarkings] Skipping planetaryLine with missing or mismatched data:`, overlay);
      }
    } else if (type === 'characteristicline') {
      if (Array.isArray(overlay.data) && overlay.data.length === barData.dates.length) {
        const metric = overlay.characteristicType || 'characteristic';
        const isMain = overlay.panel === 'main';
        let yAxisIndex = 7;
        let unit = '';
        let formatter = (v) => {
          if (v === null || v === undefined || isNaN(v)) return '';
          return Number(v).toFixed(2);
        };

        if (metric === 'speed') {
          yAxisIndex = isMain ? 10 : 7;
          unit = '°/day';
          formatter = (v) => `${Number(v).toFixed(3)}`;
        } else if (metric === 'distance_percent' || metric === 'distance_percent_diff') {
          yAxisIndex = isMain ? 11 : 8;
          unit = '%';
          formatter = (v) => `${Number(v).toFixed(1)}`;
        } else if (metric === 'longitude_diff' || metric === 'declination_diff') {
          yAxisIndex = isMain ? 12 : 9;
          unit = '°';
          formatter = (v) => `${Number(v).toFixed(2)}`;
        }

        const lineData = overlay.data.map(pt => Array.isArray(pt.value) ? pt.value : pt);
        const series = {
          type: 'line',
          name: overlay.label || overlay.characteristicType || 'Characteristic',
          data: lineData,
          symbol: 'none',
          showSymbol: false,
          connectNulls: false,
          lineStyle: {
            color: overlay.color || '#E67C73',
            width: Number(overlay.thickness || 2),
            type: overlay.lineType || 'solid',
          },
          z: 90,
          xAxisIndex: isMain ? 0 : 1,
          yAxisIndex,
          endLabel: { show: true, formatter: '{a}', color: 'inherit', offset: [0, -2], fontSize: 12, fontWeight: 'bold' },
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => {
              const val = Array.isArray(params.value) ? params.value[1] : params.value;
              return `${params.seriesName}: ${formatter(val)}${unit}`;
            }
          }
        };
        planetaryLineSeries.push(series);
      } else {
        console.warn(`[getMarkings] Skipping characteristicLine with missing or mismatched data:`, overlay);
      }
    } else if (type === 'horizontalline') {
      // Horizontal price line drawn across the full chart
      if (overlay.price !== undefined) {
        markLines.push({
          name: overlay.description || `${overlay.price}`,
          yAxis: overlay.price,
          lineStyle: { color: overlay.color || '#e53935', width: 1.5, type: 'dashed' },
          label: {
            show: true,
            position: 'insideEndTop',
            formatter: overlay.description || `${overlay.price}`,
            color: overlay.color || '#e53935',
            fontSize: 11,
          },
        });
      }
    } else if (type === 'seasonalityprojection') {
      if (Array.isArray(overlay.projections) && overlay.projections.length > 0) {
        const displayType = overlay.displayType || 'candlestick';

        // Build index-based sparse arrays for category axis alignment
        const projOhlcData = new Array(dates.length).fill('-');
        const projCloseLineData = new Array(dates.length).fill(null);

        overlay.projections.forEach(p => {
          const idx = findDateIndex(p.date, dates);
          if (idx !== -1) {
            projOhlcData[idx] = [p.projectedOpen, p.projectedClose, p.projectedLow, p.projectedHigh];
            projCloseLineData[idx] = p.projectedClose;
          }
        });

        if (displayType === 'candlestick') {
          // Projected candlestick series (semi-transparent)
          planetaryLineSeries.push({
            type: 'candlestick',
            name: 'Seasonality Projection',
            data: projOhlcData,
            barWidth: 8,
            itemStyle: {
              color: 'rgba(76, 175, 80, 0.6)',
              color0: 'rgba(244, 67, 54, 0.6)',
              borderColor: 'rgba(76, 175, 80, 0.9)',
              borderColor0: 'rgba(244, 67, 54, 0.9)',
            },
            z: 80,
          });
        }

        // Projected close line (always shown for line mode, also shown for candlestick)
        planetaryLineSeries.push({
          type: 'line',
          name: 'Projected Close',
          data: projCloseLineData,
          symbol: 'circle',
          symbolSize: 4,
          connectNulls: false,
          lineStyle: { color: '#1976d2', width: 2, type: displayType === 'line' ? 'solid' : 'dashed' },
          itemStyle: { color: '#1976d2' },
          z: 81,
        });
      }
    } else if (type === 'phaseribbon') {
      // OPERATOR FEATURES — a coloured strip locked to the BASE of the price grid.
      //
      // A `custom` series, because the ribbon is positioned in two different spaces at once: its x
      // extent is data (a run of bars) and its y position is pixels (the bottom of the pane). No
      // ordinary series can do that. `params.coordSys` hands renderItem the grid's pixel rect,
      // which is what "locked along the base" means — anchored to a PRICE instead it would slide
      // out of view the first time anyone touched the vertical zoom, which is most of what a
      // reader does with this chart.
      const runs = (Array.isArray(overlay.runs) ? overlay.runs : [])
        .filter(r => r && r.startDate !== undefined && r.endDate !== undefined);
      if (runs.length > 0) {
        const height = overlay.height || 8;
        planetaryLineSeries.push({
          id: `phase-ribbon-${overlay.id || index}`,
          name: overlay.label || 'Lifecycle phases',
          type: 'custom',
          coordinateSystem: 'cartesian2d',
          xAxisIndex: 0,
          yAxisIndex: 0,
          // Under everything. The ribbon is context for the price action, not a mark on it.
          z: 2,
          silent: true,
          clip: true,
          data: runs.map(r => ({ value: [r.startDate, r.endDate], phase: r })),
          renderItem: (params, api) => {
            const cs = params.coordSys;
            if (!cs) return null;
            const left = api.coord([api.value(0), 0])[0];
            const right = api.coord([api.value(1), 0])[0];
            const run = runs[params.dataIndex] || {};
            return {
              type: 'rect',
              shape: {
                x: Math.min(left, right),
                y: cs.y + cs.height - height,
                // At least a pixel: a one-bar run on a zoomed-out chart rounds to zero width and
                // would leave a hole in the ribbon rather than a thin mark.
                width: Math.max(1, Math.abs(right - left)),
                height,
              },
              style: { fill: run.color || '#9e9e9e' },
            };
          },
        });
      }
    } else if (type === 'trendpulseentries') {
      // TWO series, on TWO axes, because an entry signal has two halves.
      //
      // The arrow is where the trade is: on the price bar, yAxisIndex 0, below the bar for a
      // bullish entry and above it for a bearish one. The stall segment is the evidence: on the
      // TrendPulse pane, yAxisIndex 6 — the indicator's own value axis inside the price grid — so
      // it lies exactly along the two curve points that triggered the signal. Drawn against the
      // price axis it would be a horizontal line at an unrelated level.
      //
      // A branch of its own rather than an option on `pivotmarkers`, because nothing else on this
      // chart draws to both axes from one overlay.
      const entryPoints = (Array.isArray(overlay.points) ? overlay.points : [])
        .filter(p => p && p.date !== undefined && Number.isFinite(p.price));
      const stalls = (Array.isArray(overlay.paneSegments) ? overlay.paneSegments : [])
        .filter(sg => sg && sg.date1 !== undefined && sg.date2 !== undefined
          && Number.isFinite(sg.value1) && Number.isFinite(sg.value2));

      if (stalls.length > 0) {
        planetaryLineSeries.push({
          id: `trendpulse-entry-stalls-${overlay.id || index}`,
          type: 'lines',
          // Stated explicitly. A `lines` series defaults to a geo coordinate system, and on a chart
          // with no geo component it validates and then silently renders nothing at all.
          coordinateSystem: 'cartesian2d',
          xAxisIndex: 0,
          yAxisIndex: 6,
          name: `${overlay.label || 'TrendPulse entries'} — stall segments`,
          data: stalls.map(sg => ({
            coords: [[sg.date1, sg.value1], [sg.date2, sg.value2]],
            lineStyle: { color: sg.color || '#6a1b9a' },
          })),
          polyline: false,
          silent: true,
          // Heavier than the curve it sits on: the segment is a highlight, and at the curve's own
          // 1.5px it would be indistinguishable from the line it is meant to pick out.
          lineStyle: { width: 4, type: 'solid', opacity: 0.95 },
          effect: { show: false },
          z: 111,
        });
      }

      if (entryPoints.length > 0) {
        planetaryLineSeries.push({
          id: `trendpulse-entries-${overlay.id || index}`,
          name: overlay.label || 'TrendPulse entries',
          type: 'scatter',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: entryPoints.map(p => ({
            value: [p.date, p.price],
            symbol: p.symbol || 'triangle',
            symbolSize: p.symbolSize || 13,
            // Off the bar, on the side the call points away from, so the arrow never covers the
            // wick it is anchored to.
            symbolOffset: [0, (p.position === 'below' ? 1 : -1) * (p.symbolOffsetPx || 16)],
            ...(typeof p.symbolRotate === 'number' ? { symbolRotate: p.symbolRotate } : {}),
            itemStyle: { color: p.color || '#6a1b9a' },
            ...(Array.isArray(p.tooltipLines) ? { tooltipLines: p.tooltipLines } : {}),
          })),
          z: 112,
          tooltip: {
            show: true,
            trigger: 'item',
            // A SERIES-level formatter, because the chart's global one is written for candlesticks
            // and returns '' for everything else — so without this the arrows are silent however
            // much each one carries.
            formatter: (params) => {
              const lines = params?.data?.tooltipLines;
              if (!Array.isArray(lines) || lines.length === 0) return '';
              return lines.map(l => String(l).replace(/^ +/, m => '&nbsp;'.repeat(m.length)))
                .join('<br/>');
            },
          },
        });
      }
    } else if (type === 'spiralarcs') {
      // Curved vectors from each originating pivot to the convergence zone.
      //
      // A `lines` series rather than markLine, because markLine cannot curve: these vectors span
      // years, and drawn straight they are a fan of near-horizontal lines through the candles,
      // indistinguishable from a trendline study. `curveness` lifts them clear and makes the
      // "reaching forward" relationship legible, which is the entire point of drawing them.
      //
      // coordinateSystem is stated explicitly: a `lines` series defaults to a geo/map coordinate
      // system, and on a chart with no geo component it silently renders nothing at all.
      const arcs = Array.isArray(overlay.arcs) ? overlay.arcs : [];
      const arcData = arcs
        .filter(a => a && a.date1 && a.date2)
        .map(a => ({
          coords: [[a.date1, a.price1], [a.date2, a.price2]],
          // Alternating curvature so arcs sharing an endpoint stay distinguishable instead of
          // collapsing into one thick stroke — the zone is exactly where they all converge. The
          // spread widened along with the stroke weight: thicker arcs need more separation before
          // they stop overlapping.
          lineStyle: { curveness: 0.22 + 0.08 * (a.fibNumber % 4) },
          // Carried onto the data item so the series formatter can name the ORIGIN. A vector's
          // whole claim is "this bar, from THAT pivot", and the target end is the same for every
          // arc in a zone - so the origin is the only thing a floater can usefully say.
          originDate: a.originDate || a.date1,
          fibMultiple: a.fibMultiple !== undefined ? a.fibMultiple : a.fibNumber,
        }));

      if (arcData.length > 0) {
        planetaryLineSeries.push({
          // AN ID, which this series never had. Without one the tooltip whitelist cannot admit it -
          // `isTooltipAllowed(undefined)` is false - so its formatter was built and then thrown
          // away on the way out. The overlay's own id is used when it has one, so several zones'
          // arc series stay distinguishable.
          id: overlay.id || 'spiral-arcs',
          type: 'lines',
          coordinateSystem: 'cartesian2d',
          xAxisIndex: 0,
          yAxisIndex: 0,
          name: overlay.label || 'Φ vectors',
          data: arcData,
          polyline: false,
          // HOVERABLE, which it was not. `silent: true` is right for the trade connectors - there
          // are thousands of them and their markers carry the tooltip anyway - and wrong here: a
          // zone draws one arc per contributing pivot, which is a handful, and the arc is the ONLY
          // thing on the chart that knows which pivot it came from. Silent, the study could show a
          // reader six vectors and no way to ask any of them a question.
          silent: false,
          triggerLineEvent: true,
          lineStyle: {
            color: overlay.color || 'rgba(255, 152, 0, 0.85)',
            // 2.1, down 25% from the 2.8 these were widened to. The widening was to stop a thin
            // dashed stroke breaking into dots that read as chart noise across thousands of bars;
            // at 2.1 solid it is still one continuous vector, and it stops competing with the
            // candles for a chart being read by someone who has not drawn it themselves.
            width: 2.1,
            opacity: 0.95,
            type: 'solid',
            // A soft halo so the stroke stays readable where it crosses the candles, without
            // having to darken the arc itself into something that competes with the price.
            shadowBlur: 6,
            shadowColor: 'rgba(255, 152, 0, 0.55)',
          },
          effect: { show: false },
          // Under the band and the markers, which carry the reading; the arcs are context.
          z: 60,
          // A SERIES-level formatter, because the chart's global one is written for candlesticks and
          // returns '' for everything that is not one - so without this the arcs would be hoverable
          // and silent, which is worse than silent and unhoverable.
          tooltip: {
            // OFF IN SHORT BAR MODE, and off for nothing else.
            //
            // The first version of this keyed on `suppressAstroTooltips`, which sounded right and
            // hid the floater from everyone: that flag is `shortBarMode || barFloaterMode ===
            // 'ohlcv'`, and the floater mode DEFAULTS to 'ohlcv'. So the card was suppressed out of
            // the box for every user, which is exactly what was reported.
            //
            // The OHLCV floater setting is a statement about the bar floater, and every other
            // overlay respects it only when the overlay itself opts in via `astroTooltip`. This one
            // does not opt in, deliberately: a Φ vector's card says a DATE and a Fibonacci number -
            // no planet, no sign, no aspect - which is the same reasoning that puts the Integral
            // Delta markers on the whitelist as quantitative output rather than astrology.
            //
            // Short bar mode is a different kind of rule and is still absolute. The preset also
            // hides the button, so this is the second of two locks rather than the only one.
            show: !options.shortBarMode,
            trigger: 'item',
            formatter: (params) => {
              const d = params?.data || {};
              const origin = d.originDate;
              if (!origin) return '';
              // THE ORIGIN DATE is the whole content. The target end is the same for every arc in a
              // zone and is already readable off the axis under the pointer, so repeating it would
              // say the one thing the reader can see and omit the one thing they cannot: which of
              // the contributing pivots this particular vector was cast from.
              const rung = Number(d.fibMultiple);
              return `<b>Φ vector</b><br/>from the pivot on ${origin}`
                + (Number.isFinite(rung) ? `<br/>F(${rung}) lunar months forward` : '');
            },
          },
        });
      }
    } else if (type === 'pendingsignals') {
      // A scan's triggers: yellow circles, nothing has happened yet.
      //
      // Its own branch rather than `pivotmarkers`, which is close but not the same: these carry a
      // per-point tooltip, and pivotmarkers has no series-level formatter, so reusing it would draw
      // the circles correctly and make every one of them silent on hover. That is the whole value
      // of the mark — which catalyst, which direction, at what price.
      const pending = (Array.isArray(overlay.points) ? overlay.points : [])
        .filter(p => p && p.date !== undefined && Number.isFinite(p.price));
      if (pending.length > 0) {
        planetaryLineSeries.push({
          id: `integral-delta-pending-${overlay.id || index}`,
          name: overlay.label || 'Pending triggers',
          type: 'scatter',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: pending.map(p => ({
            value: [p.date, p.price],
            // FORCED, not defaulted, and unconditional on placement. A pending trigger is a
            // circle in gold whether it sits on a traded bar, a projection column, or a column it
            // was anchored to because its own date is a weekend.
            //
            // Unconditional is the point. The reported defect was projection-column markers
            // styled like executed trades, and any rule of the form "style it differently WHEN
            // extended" reintroduces that the moment the condition is wrong. There is no branch
            // here to get wrong: `p.symbol` is not read, and green and red are unreachable.
            symbol: 'circle',
            // THE GOLDEN CIRCLE, 20% down from the size the point asked for. Applied here rather
            // than at the point's own source for the same reason the colour is: this branch forces
            // the shape unconditionally, so it is the one place that knows a gold circle is what
            // will actually be drawn.
            symbolSize: goldenCircleSize(p.symbolSize || 12),
            symbolOffset: [0, (p.position === 'below' ? 1 : -1) * (p.symbolOffsetPx || 18)],
            itemStyle: {
              color: PENDING_COLOR,
              // A dark rim, because a gold dot on a pale chart background loses its edge at the
              // zoom levels this is read at.
              borderColor: '#5d4037',
              // A projection or anchored column gets a heavier rim: the mark is in a spatial slot
              // the market has not traded, and the reader is entitled to see that at a glance
              // rather than only on hover. Weight, not colour - the colour channel stays reserved.
              borderWidth: p.placement && p.placement !== 'historical' ? 2 : 1,
              // Dashed for a column the signal does not own at all.
              ...(p.anchored ? { borderType: 'dashed' } : {}),
            },
            ...(Array.isArray(p.tooltipLines) ? { tooltipLines: p.tooltipLines } : {}),
          })),
          // Above the trade decoration: a pending trigger is what the reader opened the chart for,
          // and a historical trade marker on the same bar must not sit on top of it.
          z: 115,
          tooltip: {
            show: true,
            trigger: 'item',
            formatter: (params) => {
              const lines = params?.data?.tooltipLines;
              if (!Array.isArray(lines) || lines.length === 0) return '';
              return lines.map(l => String(l).replace(/^ +/, m => '&nbsp;'.repeat(m.length)))
                .join('<br/>');
            },
          },
        });
      }
    } else if (type === 'replayplayhead') {
      // THE REPLAY PLAYHEAD: one vertical line at the bar the engine is on.
      //
      // One empty, silent series carrying a markLine. Empty because the series exists only to host
      // it; silent so it never steals a hover from the candlesticks underneath, which is where the
      // price tooltip lives.
      //
      // It used to shade everything after the playhead as well. That was removed on 2026-10-02 --
      // a full-height band whose edge moved every bar flickered and obscured the price action --
      // and replayPlayhead.js records the trade-off.
      //
      // NOT on the candlestick series. That one already carries the pattern feature's marks and a
      // second writer replaces the first — the same trap the trade-marker branch above documents.
      //
      // markLine rather than a `lines` series: it spans the grid's full height by itself, where a
      // `lines` series would need concrete y coordinates recomputed from the visible price range on
      // every zoom and pan.
      planetaryLineSeries.push({
        // The id is what lets the replay clock MOVE this series with a merge instead of
        // rebuilding the whole option — measured at ~1.2s a bar the other way.
        id: REPLAY_PLAYHEAD_SERIES_ID,
        name: overlay.label || 'DynaMan replay',
        type: 'line',
        data: [],
        xAxisIndex: 0,
        yAxisIndex: 0,
        silent: true,
        // ABOVE the candles and above the annotation series, so the line stays the topmost thing
        // on the chart -- it is the one mark a viewer has to be able to find instantly.
        z: 6,
        animation: false,
        ...playheadMarks(overlay),
      });
    } else if (type === 'timebands') {
      // THE PTA/PTB STRUCTURE: shaded vertical bands and the horizontal rays they cast.
      //
      // z BELOW everything else the replay draws. These are the BACKGROUND a reader audits the
      // trades against -- a Top line hidden behind a trade vector would defeat the purpose, and
      // a band shaded over the candles would make the price action harder to read, which is the
      // complaint that removed the playhead's veil in the first place.
      planetaryLineSeries.push({
        id: TIME_BAND_SERIES_ID,
        name: overlay.label || 'PTA / PTB time bands',
        type: 'line',
        data: [],
        xAxisIndex: 0,
        yAxisIndex: 0,
        silent: true,
        z: 2,
        animation: false,
        ...timeBandMarks(overlay.timeBands, barData.dates, overlay.upToDate),
      });
    } else if (type === 'replayannotations') {
      // THE PHYSICS, DRAWN: CTS marks, entry/exit verticals, and the live swing trendline.
      //
      // Its own series for the same reason the playhead has one — the candlestick series already
      // has two writers and a third would replace them — and because the replay clock merges this
      // by id on every bar, which it cannot do to a shared series without rewriting the candles.
      //
      // z BELOW the playhead so the amber line stays the topmost thing, and ABOVE the candles so
      // a trendline is not hidden behind a body it is being tested against.
      planetaryLineSeries.push({
        id: REPLAY_ANNOTATION_SERIES_ID,
        name: overlay.label || 'DynaMan annotations',
        type: 'line',
        data: [],
        xAxisIndex: 0,
        yAxisIndex: 0,
        silent: true,
        z: 5,
        animation: false,
        ...annotationMarks(overlay.timeline, overlay.dates, overlay.upToIndex, overlay.trendline),
      });
    } else if (type === 'ctspillars') {
      // CTS TIMING POINTS AS PILLARS, drawn into the future the bars have not reached.
      //
      // z 3: over the band shading, which is a 10%-opacity fill, and under the annotations so a
      // trendline is never hidden behind a timing pillar. These are calendar structure, in the
      // same tier as the bands and for the same reason -- a CTS date is astronomical and fixed
      // centuries ahead, so one standing in empty space ahead of the playhead leaks nothing.
      const pillars = ctsPillarMarks(overlay.points, barData.dates, overlay.upToDate);
      planetaryLineSeries.push({
        id: CTS_PILLAR_SERIES_ID,
        name: overlay.label || 'CTS timing pillars',
        type: 'line',
        data: [],
        xAxisIndex: 0,
        // THE RAIL, so this series' markPoints land at the foot of the grid rather than at a
        // price. The full-height verticals are unaffected: a markLine datum of `{xAxis: date}`
        // spans the grid whatever the y axis is, so the pillar still runs top to bottom.
        yAxisIndex: RAIL_Y_AXIS_INDEX,
        silent: true,
        z: 3,
        animation: false,
        ...pillars.rail,
      });
      // ...and its twin on the PRICE axis, for the marks whose bars have been revealed. Declared
      // unconditionally, like every other replay series: a merge addressing a series that was
      // never declared is ignored in silence, which is how an overlay comes to draw nothing at
      // all with no error anywhere.
      planetaryLineSeries.push({
        id: CTS_PILLAR_PRICE_SERIES_ID,
        name: `${overlay.label || 'CTS timing pillars'} on price`,
        type: 'line',
        data: [],
        xAxisIndex: 0,
        yAxisIndex: 0,
        silent: true,
        z: 3,
        animation: false,
        ...pillars.price,
      });
    } else if (type === 'fanlines') {
      // THE ACCELERATING FAN, drawn as it steepened.
      //
      // z 4: OVER the bands and the CTS pillars, which are background, and UNDER the annotations
      // and the playhead. The fan is a rule's working -- it belongs with the trade it manages,
      // behind the entry and exit marks that say what the rule decided.
      //
      // Declared EMPTY and merged by id on every bar, like the pillars and the markers: a merge
      // addressing a series that was never declared is ignored in silence, which is how an
      // overlay comes to draw nothing at all with no error anywhere.
      planetaryLineSeries.push({
        id: FAN_SERIES_ID,
        name: overlay.label || 'Accelerating fan',
        type: 'line',
        data: [],
        xAxisIndex: 0,
        yAxisIndex: 0,
        silent: true,
        z: 4,
        animation: false,
        ...fanLineMarks(overlay.trades, overlay.upToDate),
      });
    } else if (type === 'ledgermarkers') {
      // WHO TRADED, AND WHICH WAY: solid arrows for the engine, hollow triangles for the operator.
      //
      // z 5, beside the annotations rather than above the playhead: a marker is evidence about a
      // bar, and the amber line saying WHERE THE ENGINE IS has to stay the topmost thing on the
      // chart.
      planetaryLineSeries.push({
        id: LEDGER_MARKER_SERIES_ID,
        name: overlay.label || 'Algo / human fills',
        type: 'line',
        data: [],
        xAxisIndex: 0,
        yAxisIndex: 0,
        silent: true,
        z: 5,
        animation: false,
        ...ledgerMarkerMarks(overlay.timeline, overlay.human, barData.dates,
          overlay.upToIndex, overlay.upToDate),
      });
    } else if (type === 'integraldeltatrades' || type === 'trademarkers') {
      // TWO TYPE NAMES, ONE RENDERER. 'integraldeltatrades' is the original and is kept because
      // overlays carrying it are produced elsewhere; 'trademarkers' is the neutral name, used by
      // DynaMan. They share the drawing because a trade looks like a trade — the studies differ
      // in their exit vocabularies, which live in each study's own builder, not here.
      //
      // The overlay ID, not the type, is what distinguishes them on the chart, so both can be
      // drawn at once and removing one leaves the other alone.
      // A backtest's trades: a connector per trade, a triangle at the entry, a diamond at the exit.
      //
      // THREE series, not markPoint/markLine on the candlestick series. Those belong to the
      // candlestick and are already written by the pattern feature, so a second writer there
      // silently replaces the first — and markLine cannot carry a per-line colour derived from an
      // outcome without fighting the series' own style. Independent series also mean the whole
      // decoration is removed by dropping one overlay id.
      //
      // The connector is what makes a trade legible AS a trade: two loose markers on an
      // 11,000-bar chart are two dots with nothing saying which exit belongs to which entry.
      const connectors = (Array.isArray(overlay.connectors) ? overlay.connectors : [])
        .filter(c => c && c.date1 !== undefined && c.date2 !== undefined
          && Number.isFinite(c.price1) && Number.isFinite(c.price2));

      // THE HALF-CLICK MECHANIC. A connector that carries a `tradeKey` is split at its
      // chronological midpoint into two segments, each its own datum carrying `half`. ECharts then
      // hands the clicked half back in `params.data` -- the half is READ, never computed from pixel
      // coordinates. See tradeDecorator.js for why the alternative is wrong three ways over.
      //
      // A connector WITHOUT a tradeKey keeps exactly the old behaviour: one silent segment. The
      // sibling study produces those, and a feature that silently made its lines interactive would
      // be changing a chart nobody asked to change.
      const interactive = connectors.filter(c => c.tradeKey);
      const plain = connectors.filter(c => !c.tradeKey);
      const indexOf = dateIndex(barData && barData.dates);

      const connectorSeries = (key, data, opts = {}) => {
        if (data.length === 0) return;
        planetaryLineSeries.push({
          id: `integral-delta-connectors-${key}-${overlay.id || index}`,
          type: 'lines',
          // Stated, not defaulted. A `lines` series defaults to the GEO coordinate system, and on a
          // chart with no geo component it validates and then renders nothing at all, silently.
          coordinateSystem: 'cartesian2d',
          xAxisIndex: 0,
          yAxisIndex: 0,
          name: overlay.label || 'Integral Delta trades',
          data,
          polyline: false,
          silent: opts.silent !== false,
          lineStyle: { width: 1.5, type: 'solid', opacity: 0.55 },
          effect: { show: false },
          z: opts.z === undefined ? 110 : opts.z,
          ...(opts.silent === false ? { cursor: 'pointer' } : {}),
        });
      };

      // WIDTH and DASH travel with the colour. The builder decides the whole pen - a renderer that
      // honoured only the colour would silently flatten a deliberate two-channel encoding back to
      // one, and the loss would be invisible in a screenshot.
      const pen = c => ({
        color: c.color || '#78909c',
        ...(c.width ? { width: c.width } : {}),
        ...(c.lineType ? { type: c.lineType } : {}),
        // OPACITY travels too. The connector pen is a subtle dashed grey now, and the whole
        // point of it is the half-transparency -- which would have been dropped silently here,
        // leaving a solid grey web and no error to say why.
        ...(Number.isFinite(c.opacity) ? { opacity: c.opacity } : {}),
      });

      connectorSeries('plain', plain.map(c => ({
        coords: [[c.date1, c.price1], [c.date2, c.price2]],
        lineStyle: pen(c),
      })));

      const halfSegments = interactive.flatMap(c =>
        splitConnector(c, indexOf).map(seg => ({
          coords: seg.coords,
          // BOTH halves carry the identical pen, which is what makes the split invisible. They
          // also share an exact endpoint -- computed once in splitConnector -- so there is no
          // hairline where they meet.
          lineStyle: pen(c),
          // What a click on this segment means. Read straight off the datum by the click handler.
          tradeKey: c.tradeKey,
          half: seg.half,
          decorators: c.decorators,
        })));

      // THE VISIBLE LINE IS SILENT. The pen is 1.5px and a trade is short: on SPY's full history a
      // six-day trade is ONE PIXEL wide, so its drawn stroke is not a target a human can hit. It
      // was briefly made interactive and that is why clicking a trade line appeared to do nothing
      // -- there was no bug in the click path, there was nothing under the cursor. Measured: at
      // full zoom every segment's bounding rect is 0x0.
      connectorSeries('halves', halfSegments);

      // ...AND A SEPARATE INVISIBLE SERIES TAKES THE CLICKS.
      //
      // Same geometry, a transparent stroke, and ROUND CAPS -- which is the part that makes a
      // one-pixel trade reachable at all: a round cap turns a zero-length segment into a disc of
      // the stroke's width, so the hit area is HIT_WIDTH across however short the trade is.
      // Widening the visible line instead would have drawn a fat band over the price action, and
      // widening nothing would have left the feature unusable at any realistic zoom.
      //
      // The cost is real and bounded: a click within HIT_WIDTH of a trade line no longer reaches
      // the candle underneath. Kept to 7px -- about one candle at mid zoom -- because the
      // alternative to a modest hit area is not a smaller one, it is a feature nobody can use.
      connectorSeries('hit', halfSegments.map(seg => ({
        coords: seg.coords,
        lineStyle: { color: 'rgba(0,0,0,0)', width: TRADE_HIT_WIDTH, cap: 'round' },
        tradeKey: seg.tradeKey,
        half: seg.half,
        decorators: seg.decorators,
      })), { silent: false, z: 113 });

      // ── the decorators themselves ───────────────────────────────────────────────────────────
      //
      // DESCRIPTORS, not a series. The cards are drawn as `graphic` elements by the chart
      // component, because a `graphic` can be DRAGGED and a series label cannot -- see
      // utils/tradeCardLayout.js for the two approaches that were ruled out and why.
      //
      // What leaves this module is pure content plus the bar INDEX each card is tethered to. The
      // index rather than the date because the component converts it with `convertToPixel`, and
      // a category axis takes the index; resolving the date there would mean a second copy of the
      // date->index map that could disagree with this one.
      const openKeys = options.tradeDecorators instanceof Set ? options.tradeDecorators : null;
      if (openKeys && openKeys.size > 0) {
        interactive.forEach((c) => {
          // EXACTLY TWO halves. The combined `both` card is gone -- it anchored at the entry
          // coordinate and was therefore drawn directly over the entry card, so every trade
          // produced three cards and one of them was a guaranteed overlap carrying no new fact.
          [TRADE_HALF.ENTRY, TRADE_HALF.EXIT].forEach((half) => {
            const key = `${c.tradeKey}:${half}`;
            if (!openKeys.has(key)) return;
            const card = c.decorators && c.decorators[half];
            if (!card || !Array.isArray(card.lines) || card.lines.length === 0) return;
            const index = indexOf.get(card.date);
            if (index === undefined) return;
            tradeCards.push({
              key,
              tradeKey: c.tradeKey,
              half,
              // The leader line goes back to the MARKER -- the entry or exit triangle, which sits
              // at the fill. Not the bar's extreme: the card no longer hovers over the candle, so
              // there is nothing for an extreme to clear, and the triangle is the thing the
              // reader is being pointed at.
              index,
              price: card.price,
              date: card.date,
              // Which BAND it belongs to: a long's entry hangs below and its exit floats above,
              // and a short is the mirror. Decided where the trade's direction is known.
              side: card.side || CARD_SIDE.ABOVE,
              rich: card.rich || card.lines.join('\n'),
              lines: card.lines,
              theme: themeFor(half),
              richStyles: decoratorRich(half),
            });
          });
        });
      }

      // ── the yearly summary plaques ──────────────────────────────────────────────────────────
      //
      // Injected as ORDINARY CARD DESCRIPTORS, into the same list the trade cards travel in.
      // That is the whole strategy: `layoutCards` already places cards at the grid edges and
      // packs them, `cardGraphics` already builds a draggable `graphic`, and the chart component
      // already re-places everything on zoom. A plaque needs none of that written twice -- and
      // because it goes through the SAME packer, a plaque and a trade card cannot be laid on top
      // of each other, which a separate overlay could not have guaranteed.
      if (options.showYearlySummaries && Array.isArray(overlay.trades)) {
        tradeCards.push(...yearlySummaryCards(overlay.trades, barData && barData.dates));
      }

      // A series-level tooltip, because the chart's global formatter is written for candlesticks
      // and returns '' for anything else — without this every marker is silent.
      const tradeTooltip = {
        show: true,
        trigger: 'item',
        formatter: (params) => {
          const lines = params?.data?.tooltipLines;
          if (!Array.isArray(lines) || lines.length === 0) return '';
          return lines.map(l => String(l).replace(/^ +/, m => '&nbsp;'.repeat(m.length)))
            .join('<br/>');
        },
      };

      const markerSeries = (key, points, z) => {
        const list = (Array.isArray(points) ? points : [])
          .filter(p => p && p.date !== undefined && Number.isFinite(p.price));
        if (list.length === 0) return;
        planetaryLineSeries.push({
          id: `integral-delta-${key}-${overlay.id || index}`,
          name: `${overlay.label || 'Integral Delta trades'} — ${key}`,
          type: 'scatter',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: list.map(p => ({
            value: [p.date, p.price],
            symbol: p.symbol || 'circle',
            symbolSize: p.symbolSize || 10,
            ...(typeof p.symbolRotate === 'number' ? { symbolRotate: p.symbolRotate } : {}),
            ...(p.position
              ? { symbolOffset: [0, (p.position === 'below' ? 1 : -1) * (p.symbolOffsetPx || 14)] }
              : {}),
            itemStyle: p.hollow
              // Hollow, so an exit sitting on top of a candle does not hide it — the exit price is
              // a level on the bar, and a filled mark there obscures the thing being checked.
              ? { color: 'transparent', borderColor: p.color || '#78909c', borderWidth: 2 }
              : { color: p.color || '#78909c' },
            ...(Array.isArray(p.tooltipLines) ? { tooltipLines: p.tooltipLines } : {}),
            // THE MARKER TOGGLES ITS OWN CARD. Free: these scatter points already exist and are
            // already interactive for their tooltip, so this adds no new blocking whatsoever --
            // and at 9-13px they are the target a reader can actually hit when the whole trade is
            // a pixel wide. The line halves remain the specified mechanic for zoomed-in work.
            ...(p.tradeKey && p.half ? { tradeKey: p.tradeKey, half: p.half } : {}),
          })),
          z,
          tooltip: tradeTooltip,
        });
      };
      // Entries above exits: when a trade closes on the bar another opens, the entry is the one
      // worth clicking.
      markerSeries('exits', overlay.exits, 111);
      markerSeries('entries', overlay.entries, 112);
    } else if (type === 'aspectrail') {
      // A DIRECTION-NEUTRAL RAIL: every mark on one line at the foot of the price panel, with a
      // thin vertical through the bar it belongs to.
      //
      // WHY IT EXISTS. The same study drawn as a triangle above each bar reads as a SHORT
      // signal — a downward arrow over a high is the universal shorthand for one — and these
      // aspects carry no direction at all. Some of the bars they mark turned up. The rail says
      // "this bar, and nothing about which way", which is what the study actually knows.
      //
      // BUILT ON markLine, not on a scatter at a fixed price. A scatter needs a y in DATA space,
      // and the price axis rescales to whatever is in view — so a rail pinned to the series'
      // lowest low sits far below the window the moment anyone zooms into a later, higher
      // stretch. A markLine at `{xAxis: date}` spans the grid by construction and stays at its
      // foot at every zoom, which is the whole requirement.
      //
      // Its OWN series rather than the shared `markLines` array: that array is merged into the
      // candlestick's markLine, which forces `symbol: 'none'` and `silent: true` onto everything
      // in it — losing both the triangle and the hover card.
      const railPoints = (Array.isArray(overlay.points) ? overlay.points : [])
        .filter(p => p && p.date !== undefined);
      if (railPoints.length > 0) {
        planetaryLineSeries.push({
          id: `aspect-rail-${overlay.id || Math.random().toString(36).slice(2)}`,
          name: overlay.label || 'Aspect rail',
          type: 'scatter',
          xAxisIndex: 0,
          // THE PINNED RAIL AXIS (index 13), a fixed 0..1 scale on the price grid that is drawn
          // nowhere. See EChartComponent — the price axis rescales with the zoom, so a rail
          // placed in price space drifts off-screen; this one cannot.
          yAxisIndex: RAIL_Y_AXIS_INDEX,
          // REAL POINTS, and that is the fix for a rail that answered no hover.
          //
          // The series used to carry `data: []` and hang everything off the markLine. The line
          // drew correctly and the card never appeared: a markLine item is not a series item,
          // so there is nothing for an `item`-triggered tooltip to resolve — verified with
          // dispatchAction('showTip'), which renders the bar placement's card and cannot
          // produce this one at all.
          //
          // A point at 0.03 sits 3% up from the grid floor, carries the same fields the bar
          // placement's formatter reads, and is hit-tested exactly like the triangles that
          // demonstrably work. One hover mechanism for both placements rather than two.
          data: railPoints.map(p => ({
            value: [p.date, RAIL_Y_POSITION],
            name: p.title || '',
            tooltipLines: Array.isArray(p.tooltipLines) ? p.tooltipLines : undefined,
            tooltipFooter: p.tooltipFooter,
            label: { show: true, formatter: () => p.label || '', position: 'bottom' },
            itemStyle: {
              color: overlay.color || '#4C5B8A',
              // HOLLOW for a snapped date, matching the bar placement's own silhouette.
              opacity: p.snapped ? 0.35 : 1,
            },
          })),
          symbol: overlay.symbol || 'triangle',
          symbolSize: overlay.symbolSize || 9,
          // Pointing UP at the bar it marks, from below it. Pointing down from the foot of the
          // grid would be the bearish reading again, upside down.
          symbolRotate: 0,
          label: {
            show: true,
            position: 'bottom',
            distance: 4,
            fontSize: overlay.labelFontSize || 10,
            fontWeight: 'bold',
            ...(overlay.labelFontFamily ? { fontFamily: overlay.labelFontFamily } : {}),
            color: overlay.color || '#4C5B8A',
            backgroundColor: 'rgba(255,255,255,0.85)',
            borderRadius: 2,
            padding: [1, 3],
          },
          z: 88,
          markLine: {
            // SILENT now. The line is decoration — the scatter point above is the mark and the
            // hit target. Leaving it interactive would give one rail entry two hover targets,
            // only one of which has ever produced a card.
            silent: true,
            // NO SYMBOL. The scatter point draws the triangle now, so a symbol here would be
            // a second one a few pixels away.
            symbol: ['none', 'none'],
            lineStyle: {
              color: overlay.color || '#4C5B8A',
              // THIN and faint by design. There can be hundreds of these, and at full opacity
              // they become a picket fence that hides the price action they are annotating.
              width: overlay.railLineWidth || 1,
              opacity: overlay.railLineOpacity || 0.35,
              type: overlay.railLineType || 'solid',
            },
            // NO LABEL either — the scatter point carries it. Two labels a few pixels apart
            // is what the first version of this rail actually drew.
            label: { show: false },
            data: railPoints.map(p => ({
              xAxis: p.date,
              markLabel: p.label || '',
              name: p.title || '',
              tooltipLines: Array.isArray(p.tooltipLines) ? p.tooltipLines : undefined,
              tooltipFooter: p.tooltipFooter,
              // A SNAPPED mark is dashed as well as offset. On the rail every line reaches the
              // same foot, so the height channel the bar view uses to separate the two classes
              // is gone and the dash is what replaces it.
              ...(p.snapped
                ? { lineStyle: { type: 'dashed', opacity: 0.5 } }
                : {}),
            })),
          },
          // THE SAME CARD the bar view shows, by the same rule: short bar mode is absolute, the
          // OHLCV floater preference is opt-in. A rail whose marks could not be interrogated
          // would be strictly worse than the view it replaces.
          tooltip: {
            // The SAME rule as the bar placement, because it is the same study drawn two ways.
            // Keeping the rail on a stricter rule is how one placement came to have a card and
            // the other not -- see researchTooltip above.
            // ...and the SAME admin exemption. This was the FOURTH site to need it written out --
            // after the celestial card, the Deep Pivot card and the CTS breakdown -- which is what
            // retired the per-overlay flag in favour of `options.isAdmin`. Leaving this one out
            // would be the same discovery a fifth time.
            show: (overlay.researchTooltip || options.isAdmin || !options.shortBarMode)
              && !(overlay.astroTooltip && options.suppressAstroTooltips && !options.isAdmin),
            trigger: 'item',
            // THE SAME RENDERER as the bar placement, rather than a third copy of the promise.
            formatter: (params) => markerCardHtml((params && params.data) || {},
              { expandable: !!overlay.expandableTooltip }),
          },
        });
      }
    } else if (type === 'pivotmarkers') {
      // One overlay carries every dot of a study run, rendered as a single scatter series rather
      // than one series per dot: a study can mark dozens of pivots, and a series each would make
      // the option object enormous and the legend meaningless.
      //
      // Not attached to the candlestick series' markPoint, which the candlestick-pattern feature
      // already owns — a second writer there would silently replace the first one's dots.
      const points = Array.isArray(overlay.points) ? overlay.points : [];
      /*
       * BOTTOM-ANCHORED MODE, opt-in per overlay, built for the CTS timing marks.
       *
       * A CTS point says WHEN a turn is due and nothing about where price will be. Drawn at a
       * price — and at a price chosen from the bar's own high or low, which is where these sat —
       * the mark reads as a claim about the level, and on the replay's forward canvas it reads as
       * a claim about a bar nobody has seen. At the foot of the grid it is a tick on the
       * calendar, which is all the study ever asserted.
       *
       * <p>Pinned to {@link RAIL_Y_AXIS_INDEX}, the fixed 0..1 scale the aspect rail already
       * uses: the price axis rescales with the zoom, so "the bottom" expressed as a price drifts
       * the moment anybody zooms. A point at {@link RAIL_Y_POSITION} is three percent up from the
       * floor at every zoom by construction.
       *
       * <p>⚠ `p.position` IS STILL READ, and must be. It is the direction channel for the Trade
       * Simulator and the odds engine (`p.position === 'below' ? 'up' : 'down'` in App), so it
       * stays on the datum untouched — what changes is that it no longer decides where the mark
       * is drawn, which way it points, or what colour it is. The mark stops making a directional
       * claim; the data behind it does not change.
       */
      const railAnchored = overlay.anchor === 'bottom';
      /*
       * ⚠ PER POINT, not per overlay. This was `overlay.anchor === 'bottom'` and put the WHOLE
       * series on the rail, which over-corrected: a mark on a bar whose price is on screen has a
       * level to sit at and a direction to point, and burying it at the foot of the grid threw
       * both away for every ordinary chart.
       *
       * <p>So the rule is the one the brief states: the mark goes where the PRICE is when there
       * is one, and to the bottom when there is not. On a replay that makes it dynamic — a mark
       * sits on the rail while its bar is masked and snaps onto the chart as the playhead
       * reaches it.
       *
       * <p>Two ways a bar has no price: it is past the reveal boundary, or the point never
       * carried one (a padded future column, where `extendBarData` has no bar to read). Both
       * resolve to the rail, which is why the test fixture covers them separately.
       */
      const revealedUpTo = options.revealedUpToDate || null;
      const onRailFor = (p) => railAnchored
        && (!Number.isFinite(p.price) || (revealedUpTo !== null && p.date > revealedUpTo));

      const built = points
        // A rail mark needs no price. Requiring one on a rail-anchored overlay would silently
        // drop every point on a bar the chart has not revealed, which on the replay's forward
        // canvas is the half the pillars exist for.
        .filter(p => p && p.date !== undefined
          && (railAnchored || typeof p.price === 'number'))
        .map(p => {
          const atBottom = onRailFor(p);
          const above = atBottom ? false : p.position !== 'below';
          // ⚠ CARRIED THROUGH for the rail verticals below, which must not draw one per annotation.
          return { atBottom, cluster: !!p.cluster, datum: {
            value: [p.date, atBottom ? RAIL_Y_POSITION : p.price],
            // Offset off the bar so a dot sits clear of the wick instead of on top of it. An
            // overlay may ask for more room: markers anchored on the bar's extreme rather than its
            // close need it, and at twelve a dense run of them sits on the wicks.
            // PER-POINT when the point asks, otherwise the overlay's own. Added for the
            // celestial study, where an aspect falling on a non-trading day is snapped forward
            // to the next bar and has to be drawn HIGHER than that bar's own marks or the two
            // are indistinguishable. Absent, this is byte-identical to what every existing
            // overlay rendered.
            // ZERO for a bottom-anchored mark. The rail already sits 3% up from the floor, which
            // is the clearance this offset exists to give; adding the usual 12px would push the
            // triangle DOWN through the axis line, because `above` is false for it.
            // ⚠ A BOTTOM-ANCHORED POINT MAY NOW ASK FOR AN OFFSET, which it previously could not:
            // this was a hardcoded [0, 0]. The CTS cluster glyph needs it -- a column of dots and
            // dashes sits BESIDE the triangle, at the same bar, so it needs an x offset that the
            // rail's own placement does not provide. Defaults to [0, 0], so every existing
            // bottom-anchored overlay is unchanged.
            symbolOffset: atBottom
              ? [Number.isFinite(p.offsetXPx) ? p.offsetXPx : 0,
                 Number.isFinite(p.offsetYPx) ? p.offsetYPx : 0]
              : [0, (above ? -1 : 1)
                * (Number.isFinite(p.offsetPx) ? p.offsetPx : (overlay.symbolOffsetPx || 12))],
            // Per-point rotation, so one overlay can carry both up and down arrows — and NEVER
            // for a bottom-anchored mark. Half the triangles pointing down is a directional
            // claim, which is the one thing moving the mark off the price was meant to retract.
            ...(!atBottom && typeof p.symbolRotate === 'number'
              ? { symbolRotate: p.symbolRotate } : {}),
            // Per-point SHAPE, so one overlay can carry two kinds of mark. The series-level symbol
            // below is the fallback; without this an overlay that distinguishes its marks by shape
            // - which is what makes the distinction survive a monochrome screenshot or a
            // colour-blind reader - would silently draw them all the same.
            //
            // A point on a PROJECTION or an anchored column is forced regardless of what it asked
            // for: gold circle, never a green or red triangle. This is the chokepoint rather than
            // a rule each overlay remembers, because the defect it prevents - a forward mark
            // styled like an executed outcome - arrived through an overlay that had simply never
            // considered the case.
            // The SIZE travels with the shape and the colour, at the same chokepoint. Splitting
            // them would let a 20%-smaller circle and a full-size one both be gold depending on
            // which branch a point arrived through, which is the drift this chokepoint exists to
            // prevent. The base is the SERIES size, so a CTS triangle carrying an index digit keeps
            // all 18 of it and only the circle shrinks.
            //
            // ⚠ THE RAIL OUTRANKS THE PENDING VOCABULARY. A mark at the foot of the grid has
            // already retracted every claim the gold circle was protecting against -- it is not
            // at a price, so it cannot be read as an executed outcome -- and it is the stronger
            // statement of the two. Safe for every other overlay by construction: `atBottom` can
            // only be true on a rail-anchored one, and nothing else sets that anchor.
            ...(!atBottom && isPendingPlacement(p.placement)
              ? { symbol: 'circle', symbolSize: goldenCircleSize(overlay.symbolSize || 7) }
              : (p.symbol ? { symbol: p.symbol } : {})),
            // ⚠ AND THE SIZE, when a point asks for one. This branch resolved `p.symbol` and took
            // the SIZE from the series, so a point could change its shape but not its scale -- and
            // the CTS cluster glyphs, which are 6x1.5 dashes and 2px dots beside an 18px triangle,
            // rendered as four triangle-sized blocks. Named `symbolSizePx` rather than
            // `symbolSize` so it cannot be confused with the series-level option.
            ...(p.symbolSizePx !== undefined ? { symbolSize: p.symbolSizePx } : {}),
            itemStyle: {
              // THREE WAYS A FILL IS CHOSEN, in this order.
              //
              // On the RAIL, the point's own rail pair: a mark with no revealed price must not be
              // painted in a colour that encodes a direction, which is the whole reason it was
              // moved off the price in the first place. `railColor` is optional and falls back to
              // the ordinary one, so every overlay that has never heard of it is unchanged.
              //
              // Otherwise a pending placement still wins: a forward mark is gold whatever role it
              // carries, because "the market has not traded this column" outranks "this is a CTS
              // up arrow". The customer's colour is for the marks that mean something happened.
              color: atBottom
                ? roleColor(options, p.railColorRole || p.colorRole,
                  p.railColor || p.color || overlay.color || '#6a1b9a')
                : (isPendingPlacement(p.placement)
                  ? PENDING_COLOR
                  : roleColor(options, p.colorRole, p.color || overlay.color || '#6a1b9a')),
              // Per-point opacity, so an overlay can mark a point as provisional without spending
              // its colour channel on it. Undefined leaves ECharts' own default alone, so every
              // existing overlay renders exactly as before.
              ...(typeof p.opacity === 'number' ? { opacity: p.opacity } : {}),
              // PER-POINT RIM. Added for the PTA/PTB channel handles, where the mark sits on a
              // surface that is already its own hue — the equator line, inside a fill of the same
              // colour, between projections of the same colour again — so a single-colour symbol
              // has nothing to stand against. The rim carries the family and the fill carries the
              // visibility; see `cycleHandleColors`.
              //
              // Absent, this is byte-identical to what every existing overlay rendered: ECharts
              // defaults borderWidth to 0, so a point that asks for neither gets no rim.
              //
              // The rim goes through `roleColor` like every other colour here, so a reader who has
              // configured their own PTA or PTB hex gets a handle whose rim matches the band it
              // belongs to. Only the RIM: the fill is what makes the mark visible and must not be
              // repainted to the colour it is there to stand against.
              ...(p.borderColorRole || p.borderColor
                ? { borderColor: roleColor(options, p.borderColorRole, p.borderColor || '#000000') }
                : {}),
              ...(Number.isFinite(p.borderWidth) ? { borderWidth: p.borderWidth } : {}),
            },
            // Per-point tooltip content, for overlays that mark far too many bars to label each
            // one on the canvas. Carried on the data item and rendered by the series formatter
            // below, which is what lets a dense study say what a mark means without writing it
            // across the price action.
            ...(p.title ? { name: p.title } : {}),
            ...(Array.isArray(p.tooltipLines) ? { tooltipLines: p.tooltipLines } : {}),
            /*
             * ⚠ AND THE EXPANDED HALF, which has to be copied here or the disclosure is empty.
             *
             * Found in a browser with every unit test green: `markerCardHtml` read the field, the
             * caller set it, and the DATUM did not carry it — so 356 marks had a two-line card and
             * nothing behind the summary, and the summary correctly did not render. A field the
             * renderer reads and the datum drops is invisible from either end alone.
             */
            ...(Array.isArray(p.tooltipMoreLines)
              ? { tooltipMoreLines: p.tooltipMoreLines } : {}),
            ...(p.tooltipFooter ? { tooltipFooter: p.tooltipFooter } : {}),
            // THE SIMULATOR HOOK, and it is on the DATUM rather than keyed off the series id.
            // Any overlay that stamps `simSignal` on a point becomes clickable into the
            // Interactive Trade Simulator — which is exactly the seam PTA and PTB need later,
            // where a check against `pivot-markers-gann-confluence-arrows` would not reach them.
            ...(p.simSignal ? { simSignal: p.simSignal } : {}),
            label: p.label
              ? (p.labelInside
                // INSIDE the symbol, for a single character that identifies what the mark is - the
                // CTS confluence index. White on the symbol's own fill, with no chip behind it: a
                // background box would be wider than the symbol and would hide the shape the
                // reader is meant to see the number on.
                ? {
                    show: true,
                    position: 'inside',
                    formatter: p.label,
                    fontSize: overlay.insideLabelFontSize || 9,
                    fontWeight: 'bold',
                    color: '#ffffff',
                    // The triangle is rotated 180 for a down arrow but the digit must not be, or a
                    // 2 would read upside down.
                    ...(typeof p.symbolRotate === 'number' ? { rotate: -p.symbolRotate } : {}),
                  }
                : {
                    show: true,
                    position: above ? 'top' : 'bottom',
                    formatter: p.label,
                    // Bold and a couple of points up: these numbers are the study's output, read
                    // against candles and axis labels, and at 9px regular they were hard to pick out.
                    fontSize: overlay.labelFontSize || 12,
                    fontWeight: 'bold',
                    // OPTIONAL, and absent by default so every existing overlay keeps the chart's
                    // inherited face byte-for-byte. Asked for by the Deep Pivot tags, where the
                    // labels are fixed two-character codes (A1, B2, C3) read in a column down the
                    // chart: a proportional face sets `A1` and `B2` to different widths and the
                    // column stops reading as one.
                    ...(overlay.labelFontFamily ? { fontFamily: overlay.labelFontFamily } : {}),
                    color: p.color || overlay.color || '#6a1b9a',
                    /*
                     * ⚠ THE CHIP IS A SURFACE, AND THE LABEL'S CONTRAST IS AGAINST IT — not
                     * against the canvas.
                     *
                     * This is hardcoded white, so a study's hue is read on white whichever theme
                     * is live. That is fine for a dark hue and breaks the moment one is LIFTED for
                     * the dark canvas: `#9FA8DA` measures 8.19:1 on the dark canvas and 2.2:1 on
                     * this chip. A study that brightens itself for dark mode would therefore make
                     * its own badge unreadable — the mirror of the cycle-handle measurement, where
                     * an ink core correct on white was 1.74:1 on charcoal.
                     *
                     * Overridable rather than theme-aware HERE, deliberately: every
                     * `pivotMarkers` study in the app draws through this branch, and switching the
                     * shared chip to dark would re-decide the legibility of all of them at once.
                     * Absent means the white chip and the tight padding every existing overlay
                     * already has, byte-for-byte.
                     */
                    backgroundColor: overlay.labelChipColor || 'rgba(255,255,255,0.85)',
                    borderRadius: 2,
                    padding: overlay.labelChipPadding || [1, 4],
                  })
              : { show: false },
          } };
        });

      // PARTITIONED BY AXIS. A series has ONE `yAxisIndex`, so marks at a price and marks on the
      // rail cannot share one -- which is the whole reason this is two arrays rather than a
      // per-point y. The rail series is pushed only when it has something in it, so an ordinary
      // chart carries exactly the one series it always did.
      const data = built.filter(b => !b.atBottom).map(b => b.datum);
      const railData = built.filter(b => b.atBottom).map(b => b.datum);

      // A SECOND series for the configuration badges.
      //
      // ECharts gives a data item one label, and these marks need two that cannot share a position:
      // the index sits inside the symbol and the letters sit outside it. A second series carrying
      // the same coordinates with an invisible symbol is what lets both exist - the alternative,
      // concatenating them into one string, would put the digit and the letters in the same place
      // and lose the distinction between "what kind of confluence" and "what else was standing".
      const badgePoints = points.filter(p => p && p.badge
        && p.date !== undefined && (railAnchored || typeof p.price === 'number'));
      // Partitioned on the SAME predicate as the marks they belong to, or a badge ends up on one
      // axis while its triangle is on the other -- two heights for one mark, drifting apart on
      // every zoom.
      const badgeBuilt = badgePoints.map((p) => {
        const atBottom = onRailFor(p);
        const above = atBottom ? false : p.position !== 'below';
        return { atBottom, datum: {
          value: [p.date, atBottom ? RAIL_Y_POSITION : p.price],
          /*
           * Clear of the arrow, which already sits 12px off the bar. A bottom-anchored arrow
           * sits ON the rail, so its badge goes ABOVE it -- the only direction with room.
           *
           * ⚠ OVERRIDABLE, because 28 IS NOT ENOUGH FOR A LARGE MARK and the failure is silent.
           * This series carries `labelLayout: { hideOverlap: true }`, so a chip whose rect
           * touches the mark's own inside label is DROPPED -- no warning, no element, and a
           * study that correctly asked for `badgeVisible` simply shows nothing. Measured on the
           * pivot-commonality pair: a 20px triangle with an 11px "A" inside it spans y+12.5 to
           * y+23.5, the chip at 28 spans y+21 to y+35, they overlap by two pixels and the chip
           * vanishes. At 34 it renders.
           *
           * The DEFAULT is the old constant, so every existing overlay's badges sit exactly
           * where they did -- and any study with a symbol much over 12px needs to say so.
           */
          symbolOffset: atBottom
            ? [0, -(Number.isFinite(overlay.badgeOffsetPx) ? overlay.badgeOffsetPx : 20)]
            : [0, (above ? -1 : 1)
              * (Number.isFinite(overlay.badgeOffsetPx) ? overlay.badgeOffsetPx : 28)],
          label: {
            show: true,
            position: 'inside',
            formatter: p.badge,
            fontSize: overlay.badgeFontSize || 10,
            fontWeight: 'bold',
            /*
             * ⚠ OPT-IN, and the default of 0 is a LATENT DEFECT KEPT ON PURPOSE.
             *
             * The series sets `itemStyle.opacity: 0` to hide the 1px anchor symbol the label is
             * positioned on, and ECharts propagates that to the LABEL. Measured on a live chart:
             * a badge was in the display list, not ignored, not invisible, correctly positioned
             * at `[0, -20]` — and `style.opacity === 0`. So every configuration badge this
             * renderer has drawn since it shipped has been perfectly transparent, which is why
             * nobody reported it: an invisible label looks exactly like a feature nobody built.
             *
             * <p>Fixing it unconditionally is a one-line change and was WRONG. It put pattern
             * letters on every CTS confluence mark at once — a chart a reader has looked at for
             * months suddenly grew labels nobody asked for, which is a worse outcome than the
             * defect. So the fix is opt-in: a study that declares `badgeVisible` gets its chips,
             * and CTS keeps the appearance it has always had.
             *
             * <p>Switching the CTS badges on is one flag on `gannConfluenceOverlay`, and the card
             * behind them already exists.
             */
            opacity: overlay.badgeVisible ? 1 : 0,
            color: p.color || overlay.color || '#6a1b9a',
            backgroundColor: 'rgba(255,255,255,0.88)',
            borderColor: p.color || overlay.color || '#6a1b9a',
            borderWidth: 1,
            borderRadius: 3,
            padding: [1, 3],
          },
          ...(p.title ? { name: p.title } : {}),
          // The badge's OWN lines, not the arrow's. The arrow beside it may deliberately carry
          // nothing but a date; this is the label the breakdown was asked for on.
          ...(Array.isArray(p.badgeTooltipLines) ? { tooltipLines: p.badgeTooltipLines } : {}),
          // ...and the badge shares the arrow's expanded half, because it is the same bar. Hovering
          // the letters and hovering the triangle must not answer differently.
          ...(Array.isArray(p.tooltipMoreLines)
            ? { tooltipMoreLines: p.tooltipMoreLines } : {}),
        } };
      });
      const badgeData = badgeBuilt.filter(b => !b.atBottom).map(b => b.datum);
      const railBadgeData = badgeBuilt.filter(b => b.atBottom).map(b => b.datum);

      /*
       * OPTIONAL COMPANION VERTICALS, opt-in per overlay and built for the planetary analog
       * scanner.
       *
       * <p>A rail mark is an 18px tick at the foot of the grid. On a study that marks hundreds of
       * bars that reads as a band and the eye lands on it; on one that marks twenty-five across
       * 8,485 columns it is not findable by scanning, so a reader has to hunt for the thing they
       * came to look at. The vertical is what carries the eye from the mark up to the candle.
       *
       * <p>⚠ THE SHARED `markLines` ARRAY, which forces `silent: true` and `symbol: 'none'` onto
       * everything in it. For the aspect rail that was a reason to avoid it — it would have lost
       * the triangle and the hover card. Here both are wanted: the card belongs to the rail mark,
       * and a full-height line that stole the hover would throw a tooltip over the price action
       * every time the pointer crossed it.
       *
       * <p>Nothing has to be cleared when the overlay goes: the series are rebuilt from the
       * overlay list on every render, so removing the overlay removes these with it.
       */
      if (Array.isArray(overlay.verticals) && overlay.verticals.length > 0) {
        overlay.verticals.forEach((v) => {
          if (!v || typeof v.date !== 'string') return;
          // Dropped rather than snapped. Placement is the OVERLAY's job — it is the only thing
          // that knows what a date means in its own study — and a vertical landing on a column
          // its own mark did not would be worse than no vertical.
          if (findDateIndex(v.date, dates) === -1) return;
          markLines.push({
            xAxis: v.date,
            lineStyle: {
              color: roleColor(options, v.colorRole || overlay.colorRole,
                v.color || overlay.color || '#6a1b9a'),
              width: Number.isFinite(v.width) ? v.width : 1,
              type: v.lineType || 'dashed',
              opacity: Number.isFinite(overlay.verticalOpacity) ? overlay.verticalOpacity : 0.3,
            },
            // The rail mark already says which bar this is, and in a form that can be hovered.
            // A second label would be the same fact written across the candles.
            label: { show: false },
          });
        });
      }

      /*
       * ⚠ A VERTICAL FOR EVERY MARK THAT LANDED ON THE RAIL — opt-in, and derived rather than
       * listed, because WHICH marks are on the rail is the renderer's decision and changes.
       *
       * The rail is where a mark goes when its bar has no revealed price: the padded future
       * columns past the last real bar, and in Alpha Replay every bar past the playhead. That
       * placement is right -- a CTS point says WHEN a turn is due and nothing about the level,
       * and a triangle at a price nobody has seen would claim one.
       *
       * <p>What it costs is FINDABILITY, and the Critical Timing Scan is the case that shows it:
       * twelve forward marks across 250 padded columns, 18px high, at the foot of a grid whose
       * price action is several hundred pixels above. Measured on SPY they are drawn correctly
       * and are genuinely hard to see, which reads as the forward half of the study missing.
       * The analog scanner already records the same finding for its own rail marks.
       *
       * <p>So the vertical carries the eye from the floor up to the chart. It adds NO label --
       * `label: { show: false }`, like every other vertical here -- so a study that was never
       * meant to grow text on its marks does not.
       *
       * <p>Derived from the BUILT points rather than from a date list, because in a replay the
       * rail set moves with the playhead: a mark snaps onto the chart as its bar is revealed and
       * its vertical must go with it. A list computed at scan time could not follow that.
       */
      if (overlay.railVerticals) {
        /*
         * ⚠ PAST THE LAST TRADED BAR ONLY, NOT EVERY RAIL MARK -- and the difference is a
         * regression this nearly shipped twice.
         *
         * Two things put a mark on the rail and `onRailFor` cannot tell them apart: a padded
         * column the market has not traded, and a column Alpha Replay is hiding behind the
         * playhead. The first is permanent; the second resolves as the reader steps forward.
         *
         * Drawn for both, a replay opened at bar 1 of SPY would hatch the chart with ~356
         * full-height lines -- and the replay ALREADY draws its own CTS pillars, so they would
         * be a duplicate forest on top of a correct one. `ctsPillars` carries that job and
         * caps its own count for exactly this reason.
         *
         * <p>⚠ AND AN ANNOTATION IS NOT A MARK. A point flagged `cluster` is one glyph of a CTS
         * cluster column -- it shares its triangle's DATE, so each one would push a SECOND line
         * at the same x. Four of them at 0.22 opacity compound to about 0.7, and the one forward
         * mark that happened to carry a column would show a vertical three times darker than its
         * neighbours -- which reads as a property of that date rather than as a drawing artefact.
         * Caught before it shipped by reading this block rather than by looking at the chart,
         * where it would have been visible only on the forward half and only sometimes.
         *
         * <p>⚠ AND THE TEST CANNOT BE "has no price". `extendBarData` pads with the last real
         * CLOSE repeated into all four columns, so a padded bar has a perfectly finite price
         * and the first version of this drew NOTHING -- twelve correct marks and zero lines,
         * found by reading the live option after the fix was supposed to be in. What makes a
         * column forward is its DATE against the last traded bar, which is the one fact a
         * playhead does not move.
         */
        const lastTraded = options.lastTradedDate || null;
        built.filter(b => b.atBottom && !b.cluster
          && (lastTraded === null || b.datum.value[0] > lastTraded)).forEach((b) => {
          markLines.push({
            xAxis: b.datum.value[0],
            lineStyle: {
              // The RAIL colour, which for a rail-anchored overlay is the one the mark itself
              // is drawn in -- a vertical in the price-mark's direction colour would reassert
              // the direction the rail placement exists to withhold.
              color: b.datum.itemStyle.color,
              width: Number.isFinite(overlay.railVerticalWidth) ? overlay.railVerticalWidth : 1,
              type: 'dashed',
              opacity: Number.isFinite(overlay.railVerticalOpacity)
                ? overlay.railVerticalOpacity : 0.22,
            },
            label: { show: false },
          });
        });
      }

      const key = overlay.id || Math.random().toString(36).slice(2);
      /*
       * ONE SERIES PER AXIS. A series carries a single `yAxisIndex`, so marks at a price and
       * marks on the rail cannot share one — which is why this is a loop rather than a
       * per-point y. The rail half is emitted only when it has points, so an overlay with
       * nothing masked produces exactly the one series it always did.
       *
       * <p>The suffix matters: `removeOverlay` clears by id prefix and a merge addresses a
       * series by id, so a rail series sharing the marker series' id would replace it.
       */
      [
        { suffix: '', seriesData: data, badges: badgeData, yAxis: 0 },
        { suffix: '-rail', seriesData: railData, badges: railBadgeData,
          yAxis: RAIL_Y_AXIS_INDEX },
      ].forEach(({ suffix, seriesData, badges, yAxis }) => {
      if (seriesData.length > 0) {
        planetaryLineSeries.push({
          id: `pivot-markers-${key}${suffix}`,
          name: overlay.label || 'Pivot markers',
          // effectScatter is ECharts' own pulsing variant of the same series - identical data,
          // identical symbols, with a ripple under each mark. Used rather than a CSS animation
          // because these symbols are drawn on a canvas: there is no DOM node per arrow to attach
          // a keyframe to, and driving symbolSize from a timer would re-render the whole option on
          // every frame.
          type: overlay.pulse ? 'effectScatter' : 'scatter',
          ...(overlay.pulse ? {
            rippleEffect: { period: 3, scale: 3.2, brushType: 'stroke' },
            // The ripple is the point; a symbol that also grows and fades makes it mush.
            showEffectOn: 'render',
          } : {}),
          xAxisIndex: 0,
          // The pinned 0..1 rail for the masked half, the price axis for the revealed one. Both
          // are on grid 0, so the mark is in the price pane either way — what differs is whether
          // its height is a price or a fraction of the grid.
          yAxisIndex: yAxis,
          data: seriesData,
          // 'emptyCircle' is ECharts' outlined variant, which is what makes one tool draw a ring
          // and the other a disc from the same renderer.
          symbol: overlay.symbol || 'circle',
          symbolSize: overlay.symbolSize || 7,
          z: 90,
          // ECharts drops the labels that would collide and keeps the rest, re-deciding on every
          // zoom. It is what makes a per-mark label survivable at all on this overlay: a full-chart
          // CTS run draws up to 400 arrows across ~1400px, which is 3.5px each, and a label on
          // every one collapses into an unreadable band across the price action - measured, at 400
          // marks on a 33-year SPY chart, which is why the previous canvas label was removed
          // entirely. Hiding overlaps means zooming out shows a few and zooming in reveals them
          // all, with the tooltip carrying everything at every zoom.
          labelLayout: { hideOverlap: true },
          // A SERIES-level formatter, because the chart's global one is written for candlesticks
          // and returns '' for everything else - so without this a marker's tooltip is blank
          // however much the point carries. A series formatter takes precedence for its own items.
          //
          // SUPPRESSED ENTIRELY when this overlay's card is astrological and the reader has asked
          // for the OHLCV floater alone. `show: false` rather than a formatter returning '': an
          // empty string still opens an empty box that follows the pointer and covers the candles,
          // which is the obstruction being removed rather than a smaller version of it.
          tooltip: {
            // TWO DIFFERENT RULES, asked apart.
            //
            // SHORT BAR MODE is a product constraint and is absolute: nothing astrological may
            // reach the screen, whatever the overlay thinks. A series formatter takes precedence
            // over the chart's global one, so the global short-bar lock never runs for this
            // series -- this is the lock for it.
            //
            // The OHLCV FLOATER PREFERENCE is a reader's setting and stays OPT-IN via
            // `astroTooltip`. `suppressAstroTooltips` is `shortBarMode || floater === 'ohlcv'`
            // and the floater DEFAULTS to 'ohlcv', so anything keyed on it is off for everybody
            // out of the box -- right for a planetary hover card, wrong for a study whose card
            // is a date and two aspect names. The celestial and Deep Pivot cards hit exactly
            // that and appeared for nobody.
            //
            // RESEARCH TOOLTIP is the exception to the short-bar lock, and it is narrow on
            // purpose. The lock exists so a CUSTOMER-FACING preset exposes nothing
            // astrological; an admin-only research study has no customer to expose it to -- the
            // dialog that draws these returns null outright for a non-admin. Without the
            // exception the study is unusable in that preset and looks broken rather than
            // restricted: the triangles draw, the labels draw, and hovering does nothing.
            // STATS TOOLTIP is the second exception, and it is narrow for a different reason
            // than the first.
            //
            // The short-bar lock above is a PRODUCT CONSTRAINT and stays absolute. The astro
            // suppression is a READER PREFERENCE -- "I want the OHLCV floater, not planetary
            // hover cards" -- and it is keyed on what the card SAYS. A card carrying reversal
            // odds, a sample size and a base rate is not a planetary card, so the preference
            // has nothing to suppress: a reader who turned astrological cards off did not ask
            // to turn statistics off.
            //
            // Without this the CTS odds tooltip would be hidden FOR EVERYBODY, because
            // `barFloaterMode` defaults to 'ohlcv'. That is the exact failure recorded for the
            // celestial and Deep Pivot cards -- the marks draw, the labels draw, and hovering
            // does nothing.
            // ⚠ AND AN ADMINISTRATOR IS AN EXCEPTION TO BOTH, which is a general rule rather
            // than a third per-overlay flag.
            //
            // It was a per-overlay flag for about an hour. The reason it is not is that the same
            // exemption had already been written three times for three different cards -- the
            // celestial study, Deep Pivot, and the CTS aspect breakdown -- each discovered the
            // same way, by a card shipping invisible. `barFloaterMode` defaults to 'ohlcv', so
            // every rule keyed on `suppressAstroTooltips` is on for everybody out of the box, and
            // a fourth card would have been the fourth discovery.
            //
            // `statsTooltip` could NOT carry it: its whole argument is that the card says odds and
            // a base rate rather than planets, and a CTS aspect breakdown says planets. Reusing it
            // would be a lie about the content AND would un-suppress these cards for customers.
            //
            // ⚠ THE COST, stated: an administrator who genuinely wants the OHLCV floater and no
            // hover cards gets them anyway. The exemption is confined to whether a card may OPEN
            // -- it touches no product deny-list, no role gate and nothing a customer can see.
            show: (overlay.researchTooltip || options.isAdmin || !options.shortBarMode)
              && !(overlay.astroTooltip && options.suppressAstroTooltips
                && !overlay.statsTooltip && !options.isAdmin),
            // Matched to the spiral arcs. The chart's global tooltip is `trigger: 'item'`, so
            // this is belt and braces rather than a fix -- but it is the one difference between
            // this series and the arcs, which are known to work.
            trigger: 'item',
            /*
             * ⚠ OPT-IN, for a card that hangs off a mark at the FOOT of the grid.
             *
             * ECharts places a tooltip below-right of the pointer and lets it leave the chart.
             * For a mark on the rail there is no room below: the planetary analog card measured
             * 880x522 at y=1074 in a 1271-high window, so its last 325px -- the forward-return
             * table and the snapped-date note -- were off the bottom of the screen. Found by
             * measuring the rendered card's rect, not by looking at it.
             *
             * `confine` keeps it inside the chart's own rectangle. Left OFF by default so every
             * existing study renders exactly as it did: this changes WHERE a card appears, and
             * a short card near the top of the grid is better off free.
             */
            ...(overlay.confineTooltip ? { confine: true } : {}),
            /*
             * ⚠ ENTERABLE ONLY WHEN THERE IS SOMETHING TO EXPAND, and opt-in per overlay.
             *
             * `enterable` lets the pointer move onto the card, which is what makes the disclosure
             * clickable — and it also stops the card vanishing when the pointer leaves the mark.
             * On a study that marks hundreds of bars that is a real change to how hovering feels,
             * so it is not switched on for overlays that have nothing behind a summary.
             */
            ...(overlay.expandableTooltip ? { enterable: true } : {}),
            formatter: (params) => markerCardHtml((params && params.data) || {},
              { expandable: !!overlay.expandableTooltip }),
          },
        });

        if (badges.length > 0) {
          planetaryLineSeries.push({
            id: `pivot-badges-${key}${suffix}`,
            name: `${overlay.label || 'Pivot markers'} badges`,
            type: 'scatter',
            xAxisIndex: 0,
            // The SAME axis its marks are on, or the badge and the triangle it belongs to end up
            // at two heights -- one a price, one a fraction of the grid -- and drift apart on
            // every zoom.
            yAxisIndex: yAxis,
            data: badges,
            // A symbol is still needed - a series with symbol 'none' renders no label either - so
            // it is drawn at one pixel and fully transparent. The label is the whole point of the
            // series; the symbol is only what anchors it.
            symbol: 'circle',
            symbolSize: 1,
            itemStyle: { opacity: 0 },
            // Above the arrows, so a badge is never painted under the mark it belongs to.
            z: 91,
            labelLayout: { hideOverlap: true },
            // The same card as the arrow. Hovering the letters and hovering the triangle are the
            // same question about the same bar, and two different answers would be a bug the reader
            // could only find by trying both.
            tooltip: {
              show: true,
              ...(overlay.expandableTooltip ? { enterable: true } : {}),
              // THE SAME RENDERER as the arrow, not the same promise written twice.
              formatter: (params) => markerCardHtml((params && params.data) || {},
                { expandable: !!overlay.expandableTooltip }),
            },
          });
        }
      }
      });
    } else if (type === 'cyclelines') {
      /*
       * EVERY HORIZONTAL LINE OF ONE CYCLE, on ONE series.
       *
       * <p>Each line is a `markLine` coord PAIR rather than a series of its own, and that is a
       * performance decision with a hard number behind it: a PTB run on SPY draws 140 bands, and
       * at seven lines each a series apiece would be 980 ECharts series on a chart that has
       * eight. The replay's band overlay carries its rays the same way for the same reason.
       *
       * <p>SILENT and below the price: these are a background a reader audits price action
       * against, like the bands they belong to. A tooltip on a guide would also be a tooltip on
       * the 980 of them.
       */
      if (Array.isArray(overlay.lines) && overlay.lines.length > 0) {
        const colour = roleColor(options, overlay.colorRole, overlay.color || '#888888');
        planetaryLineSeries.push({
          id: overlay.id,
          name: overlay.label || 'Cycle guides',
          type: 'line',
          data: [],
          xAxisIndex: 0,
          yAxisIndex: 0,
          silent: true,
          z: 2,
          animation: false,
          markLine: {
            silent: true,
            symbol: 'none',
            animation: false,
            // No label on any of them. The replay's rays are labelled because there are three per
            // scope; there are seven here per band and 980 per run, and a label on each would be
            // the only thing on the chart.
            label: { show: false },
            data: overlay.lines.map(line => ([
              {
                coord: [line.date1, line.price],
                lineStyle: { color: colour, width: line.width, type: line.dash },
              },
              { coord: [line.date2, line.price] },
            ])),
          },
        });
      }
    } else if (type === 'tradesimvectors') {
      /*
       * THE INTERACTIVE TRADE SIMULATOR'S OWN BOOK, drawn as one segment per completed trade.
       *
       * <p>Built by `tradeSimVectors.js`, which decides WHICH trades are drawable and in what
       * colour; this branch only renders. The split is the usual one here — a renderer that
       * decided whether a trade had an exit would be a second answer to a question the ledger has
       * already answered, and the two would disagree on the one case nobody tested.
       *
       * <h3>SILENT, and that is load-bearing rather than tidy</h3>
       *
       * The chart's price grid carries a two-zone click model: on a revealed candle is a TRADE
       * action and anywhere else advances the replay playhead. A non-silent series laid over the
       * candles would win the hit test along its own length, so a reader clicking the bar a vector
       * happens to cross would get neither gesture. Every vector is drawn across bars the reader
       * may still want to click.
       *
       * <h3>One series, coord PAIRS</h3>
       *
       * A session can run to dozens of trades, and a series each would be dozens of ECharts series
       * on a chart that has eight. The same arrangement the cycle guides and the replay's rays use.
       */
      if (Array.isArray(overlay.lines) && overlay.lines.length > 0) {
        planetaryLineSeries.push({
          id: overlay.id,
          name: overlay.label || 'Session trades',
          type: 'line',
          data: [],
          xAxisIndex: 0,
          yAxisIndex: 0,
          silent: true,
          // ABOVE the price, unlike the cycle guides at z 2: a guide is a background a reader
          // audits price against, and this is a record of something they DID.
          z: 7,
          animation: false,
          markLine: {
            silent: true,
            // A dot where the trade opened and an arrow where it closed, so the segment carries
            // the direction of TIME. Without it a vector is reversible by eye, and for a short —
            // where the profitable line slopes down — that is the difference between reading a
            // winner and a loser.
            symbol: ['circle', 'arrow'],
            symbolSize: [5, 7],
            animation: false,
            // No label on any of them. A session of thirty trades would otherwise write thirty
            // captions across the price action it is drawn over.
            label: { show: false },
            data: overlay.lines.map(line => ([
              {
                coord: [line.date1, line.price1],
                name: line.title || '',
                lineStyle: {
                  color: line.color,
                  width: TRADE_SIM_VECTOR_WIDTH,
                  type: 'solid',
                  opacity: TRADE_SIM_VECTOR_OPACITY,
                },
                itemStyle: { color: line.color, opacity: TRADE_SIM_VECTOR_OPACITY },
              },
              {
                coord: [line.date2, line.price2],
                itemStyle: { color: line.color, opacity: TRADE_SIM_VECTOR_OPACITY },
              },
            ])),
          },
        });
      }
    } else if (type === 'analyticascope') {
      /*
       * THE RANGE CHART ANALYTICA SCANNED, and the bar it stopped at.
       *
       * <p>An xAxis-ONLY markArea pair, which in ECharts means FULL HEIGHT — correct here and
       * wrong for a channel, because what is being shaded is a stretch of TIME with no price
       * extent. The cycle tint had to be converted from this shape to a coord pair for exactly
       * the opposite reason.
       *
       * <p>It survives the annotation toggle. Hiding it with the markers would leave a dashboard
       * describing a range that nothing on the chart identifies.
       */
      const startIndex = findDateIndex(overlay.date1, dates);
      const endIndex = findDateIndex(overlay.date2, dates);
      if (startIndex !== -1 && endIndex !== -1) {
        markAreas.push([
          { xAxis: dates[Math.min(startIndex, endIndex)],
            itemStyle: { color: CHART_ANALYTICA_SCOPE_FILL } },
          { xAxis: dates[Math.max(startIndex, endIndex)] },
        ]);
      }
      const clickIndex = findDateIndex(overlay.clickedDate, dates);
      if (clickIndex !== -1) {
        markLines.push({
          xAxis: dates[clickIndex],
          // The one line in this study that is a STATEMENT rather than a background, so it is
          // the only one that carries a label — a dashed grey vertical with no caption is
          // indistinguishable from the audit lines it sits among.
          lineStyle: { color: CHART_ANALYTICA_CLICK, width: 1.5, type: 'solid', opacity: 0.9 },
          label: {
            show: true, position: 'insideEndTop', formatter: 'Analytica',
            fontSize: 10, color: CHART_ANALYTICA_CLICK, fontWeight: 'bold',
          },
        });
      }
    } else if (type === 'analyticaaudit') {
      /*
       * A DASHED VERTICAL through every audited bar.
       *
       * <p>Coloured by the event's own direction so the chart reads without the markers, and
       * drawn at the study's own low opacity: these pass behind the price action and must stay
       * behind it. They are capped upstream by `analyticaOverlays`, which also reports the cap.
       */
      (Array.isArray(overlay.lines) ? overlay.lines : []).forEach((line) => {
        if (!line || typeof line.date !== 'string') return;
        if (findDateIndex(line.date, dates) === -1) return;
        markLines.push({
          xAxis: line.date,
          lineStyle: {
            color: line.direction === 'down' ? CHART_ANALYTICA_DOWN : CHART_ANALYTICA_UP,
            width: AUDIT_LINE_WIDTH,
            type: 'dashed',
            opacity: AUDIT_LINE_OPACITY,
          },
          label: { show: false },
        });
      });
    } else if (type === 'drawrect') {
      // Rectangle drawn between two diagonal corners (date1,price1) and (date2,price2)
      const startIndex = findDateIndex(overlay.date1, dates);
      const endIndex = findDateIndex(overlay.date2, dates);
      if (startIndex !== -1 && endIndex !== -1) {
        const d1 = dates[Math.min(startIndex, endIndex)];
        const d2 = dates[Math.max(startIndex, endIndex)];
        const p1 = Math.min(overlay.price1, overlay.price2);
        const p2 = Math.max(overlay.price1, overlay.price2);
        markAreas.push([
          // borderType lets a congested band draw a dotted edge — see utils/bandDensity. ECharts
          // defaults it to 'solid', so overlays that do not set it are unaffected.
          { xAxis: d1,
            yAxis: p2,
            itemStyle: {
              color: roleColor(options, overlay.colorRole, overlay.color || 'rgba(255,152,0,0.18)'),
              // The BORDER keeps the study's own stroke. One configurable colour per element, as
              // specified - and a band whose fill and edge were both the customer's choice would
              // lose the edge entirely the moment they picked an opaque fill.
              borderColor: overlay.borderColor || '#ff9800',
              // 1.5 UNLESS THE OVERLAY ASKS OTHERWISE, which is new and is why `??` rather than
              // `||`: zero is the whole point of asking. It was hardcoded, so every rect drew an
              // outline whether or not an outline said anything - and for a PROJECTED range,
              // which is a companion to a measured band rather than a measurement of its own,
              // the outline was most of what a reader could see of it.
              borderWidth: overlay.borderWidth ?? 1.5,
              borderType: overlay.borderType || 'solid',
            } },
          { xAxis: d2, yAxis: p1 },
        ]);
      }
    }
  });

  // --- BEGIN FINAL DIAGNOSTIC LOGGING ---
  console.log('[getMarkings] Final markLines:', markLines);
  console.log('[getMarkings] Final markAreas:', markAreas);
  console.log('[getMarkings] Final trendLines:', trendLines);
  console.log('[getMarkings] Final planetaryLineSeries:', planetaryLineSeries);
  // --- END FINAL DIAGNOSTIC LOGGING ---
  return { markLines, markAreas, trendLines, planetaryLineSeries, tradeCards };
};

import { detectAllPatterns } from './candlestickPatterns';
import { formatTrendlineBadge, MANUAL_DRAW_SOURCE, badgeSidesByOverlayId } from './trendlineMeasure';
import { playheadMarks, REPLAY_PLAYHEAD_SERIES_ID } from './replayPlayhead';
import { timeBandMarks, TIME_BAND_SERIES_ID } from './timeBandOverlay';
import { annotationMarks, REPLAY_ANNOTATION_SERIES_ID } from './replayAnnotations';
import { ledgerMarkerMarks, LEDGER_MARKER_SERIES_ID } from './ledgerMarkers';
import { RAIL_Y_AXIS_INDEX, RAIL_Y_POSITION } from './chartRail';
import { ctsPillarMarks, CTS_PILLAR_SERIES_ID, CTS_PILLAR_PRICE_SERIES_ID } from './ctsPillars';
import { fanLineMarks, FAN_SERIES_ID } from './fanLines';

function getPatternColor(patterns) {
  if (!patterns || patterns.length === 0) return '#e53935';
  const pattern = patterns[0];
  switch (pattern) {
    case 'Hammer': return '#ff9800';
    case 'Doji': return '#9c27b0';
    case 'Bearish Engulfing Pattern': return '#e53935';
    case 'Bullish Marubozu': return '#2196f3';
    case 'Bearish Marubozu': return '#f44336';
    case 'Three Black Crows': return '#607d8b';
    case 'Three White Soldiers': return '#4caf50';
    case 'Morning Star': return '#00bcd4';
    case 'Evening Star': return '#ff5722';
    case 'Morning Doji Star': return '#00bcd4';
    case 'Evening Doji Star': return '#ff5722';
    default: return '#e53935';
  }
}

// patternVisibility is an object mapping pattern names to booleans
// maConfigs: array of { period, color, enabled }
/**
 * Series ids whose own hover card is allowed through when `restrictTooltips` is on.
 *
 * <p>The candlestick is NOT here: it carries no series-level tooltip and is served by the chart's
 * global formatter, which already returns '' for everything that is not a candlestick. So the two
 * layers together are the whitelist — the global one admits price data, this one admits the
 * Integral Delta markers, and nothing else has a route to the screen.
 *
 * <p>Pending triggers are on the list alongside the trade markers. They are quantitative output,
 * not astrology, and their whole purpose is to be hovered: a yellow circle with no catalyst, no
 * direction and no trigger price is a dot. Blocking them would undo the feature rather than
 * declutter it.
 */
/**
 * The y-axis the rail is drawn on, and where on it the marks sit — RE-EXPORTED.
 *
 * <p>Declared in `chartRail`, which owns them and imports nothing. They were declared HERE, and
 * the moment `ctsPillars` needed the position the two modules imported each other: this one
 * imports `ctsPillarMarks`. An ES-module cycle does not fail loudly — see `chartRail` for what it
 * does instead — so the constants moved to a leaf and are re-exported, because they were part of
 * this module's surface and tests import them from here.
 */
export { RAIL_Y_AXIS_INDEX, RAIL_Y_POSITION } from './chartRail';

export const TOOLTIP_ALLOWED_PREFIXES = [
  'integral-delta-entries',
  'integral-delta-exits',
  'integral-delta-pending',
  // THE Φ VECTORS. Added because a formatter alone did nothing: `restrictTooltips` is on
  // UNCONDITIONALLY - it is not tied to short bar mode, despite reading like a short-bar rule -
  // so every series not named here leaves with `tooltip: { show: false }`, overwriting whatever it
  // had built. The arcs series carried no `id` at all, so it could not have been whitelisted even
  // deliberately.
  //
  // The short-bar rule is still honoured, and NOT by this list: the arcs attach their formatter
  // only when `suppressAstroTooltips` is off, which is the flag that actually means "nothing
  // astrological may reach the screen". A whitelist entry and a security rule are different jobs
  // and this list was quietly doing both.
  'spiral-arcs',

  // THE DEEP PIVOT TAGS and THE CELESTIAL ASPECT TRIANGLES, for the same reason as the arcs and
  // discovered the same way: by someone hovering one and getting nothing.
  //
  // Both build a full per-point card — the aspects, their orbs, the exact instants — and both
  // then left here with `tooltip: { show: false }` because the id was not on this list. The unit
  // tests behind them called the series formatter DIRECTLY and passed, which proves the content
  // and says nothing about whether the card is ever shown. `restrictTooltipsHonoursTheWhitelist`
  // now drives the real `restrictTooltips` path instead.
  //
  // The SHORT-BAR rule is not delegated to this list. Both overlays set `astroTooltip`, so the
  // renderer withholds their formatter whenever `suppressAstroTooltips` is on — which is the flag
  // that actually means "nothing astrological may reach the screen". A whitelist entry and a
  // security rule are different jobs, and conflating them is what this list was doing before.
  'pivot-markers-deep-pivot-markers',
  'pivot-markers-celestial-aspect-markers',
  // The same study drawn as a bottom rail -- a different series id, so the
  // whitelist entry above does not cover it and the card would vanish again the
  // moment a reader switched placement.
  'aspect-rail-celestial-aspect-markers',
  // THE RANDOM VISUAL BASELINE, in both placements. Its card is the one thing that says
  // "Source RANDOM -- not a computed aspect" on hover, so suppressing it would leave grey
  // triangles a reader could interrogate no further than their colour.
  'pivot-markers-celestial-random-baseline',
  'aspect-rail-celestial-random-baseline',

  // THE CTS MARKS, for the LEFT-ONLY REVERSAL ODDS card — and this list has now caught the same
  // mistake four times, in exactly the way the entries above describe: the marks draw, the
  // labels draw, the points carry a fully built card, and hovering does nothing.
  //
  // It is worth being precise about why a whitelist entry is needed on top of the `show`
  // expression in the pivotmarkers branch, because the two look redundant and are not:
  //
  //   this list            MAY this series show a card at all        a declutter rule
  //   the `show` clause    SHOULD this card be shown right now       the astro / short-bar rules
  //
  // `restrictTooltips` runs LAST and overwrites whatever the branch decided, so a series that
  // gets `show: true` from a correct `show` expression still leaves with `show: false` unless
  // it is named here. Four studies have now been built, tested and shipped blind to that.
  //
  // The card itself is statistical — odds, a sample size, a base rate and an interval — which is
  // what `statsTooltip` says, and that flag is what keeps the OHLCV-floater preference from
  // suppressing it. The BADGE series beside this one is deliberately NOT listed: its card is the
  // aspect detail, which is astrological and stays off.
  'pivot-markers-gann-confluence-arrows',

  // THE MIDPOINT TRINE SCAN and THE ECLIPSE STUDY. The FIFTH and SIXTH times, found together by
  // hovering an eclipse mark and getting nothing, then checking the midpoint id against the
  // prefixes above and finding it matches none either — so that study has been drawing marks
  // that answer no hover since the day it shipped.
  //
  // Both are ADMIN RESEARCH reached only from the Admin Panel, which renders nothing for a
  // non-admin, so there is no customer for the short-bar rule to protect: both set
  // `researchTooltip`, which is what exempts them from the `show` clause. This list is the other
  // gate, and a study needs both.
  //
  // The eclipse prefix covers the `-rail` twin by construction, since the renderer appends that
  // suffix — the celestial study needed two entries only because its two placements are two
  // different ids rather than one with a suffix.
  'pivot-markers-midpoint-trine-markers',
  'pivot-markers-eclipse-study-markers',

  // THE PLANETARY ANALOG SCANNER. Added in the same edit as the overlay rather than after
  // someone hovered a mark and got nothing — which is how the six entries above arrived, and
  // the midpoint-trine one only after it had been drawing hover-dead marks since the day it
  // shipped.
  //
  // Its card is the WHOLE feature here: the chart says which bar, and the card says what agreed
  // on it and by how much. Marks that answer no hover would leave a reader with twenty-five
  // pink ticks and no way to interrogate any of them.
  //
  // Research, admin-only, so `researchTooltip` handles the short-bar `show` clause; this list is
  // the other gate and a study needs both.
  'pivot-markers-planetary-analog-markers',
  // ⚠ AND ITS BADGES, which are a SEPARATE SERIES and therefore a separate entry. These carry
  // the SAME card as the mark they sit on, because the chip and the symbol are one question about
  // one bar and two answers would be a bug found only by trying both.
  //
  // ⚠ THE CTS BADGES ARE ABSENT FOR A DIFFERENT REASON THAN THEY ONCE WERE. The old note here
  // said "their card is astrological detail", which was true when the breakdown lived on the
  // badge and the arrow carried only a date -- neither is true now: the arrow carries the reason
  // and the whole expanded half, and `gannConfluenceOverlay` does not set `badgeVisible`, so the
  // CTS chip is drawn at `opacity: 0`. A card on an invisible chip is unreachable, so adding it
  // here would buy nothing until the chips are switched on.
  //
  // Easy to miss: the badge renders, the chip reads `A7 HEL`, and hovering the three letters —
  // which is the natural gesture once the type is on the canvas — does nothing.
  'pivot-badges-planetary-analog-markers',

  // THE PIVOT PLANETARY COMMONALITIES STUDY, both series, added in the same edit as the overlay.
  //
  // This one draws exactly TWO marks -- the pair a reader selected in the matrix -- so the card
  // carries nearly everything the row did: both pivots, their kinds and prices, the span, and
  // the commonalities themselves. Silent, the pair would be two amber triangles and a vertical,
  // which says which bars and nothing about why they are a pair.
  //
  // Research, admin-only, so `researchTooltip` answers the short-bar `show` clause and
  // `statsTooltip` answers the OHLCV-floater preference. This list is the third gate.
  'pivot-markers-pivot-commonality-pair',
  'pivot-badges-pivot-commonality-pair',

  // TIME PROJECTION, added in the SAME EDIT as the overlay rather than after somebody hovered a
  // mark and got nothing — which is how the seven entries above arrived, two of them only after
  // the study had been shipping hover-dead marks for weeks.
  //
  // The card is the whole of this tool's explanation. The canvas shows an arrow on the rail and
  // nothing else; the card is what says WHICH offset landed there, which digits of the close
  // produced it, and whether the projected day was snapped forward off a weekend. Silent, three
  // indigo arrows say "something is due here" and give a reader no way to check the arithmetic.
  //
  // Admin research reached from the Admin Panel, so `researchTooltip` answers the short-bar
  // `show` clause. This list is the other gate and a study needs both. It carries no badge
  // series, so there is no second entry to add.
  'pivot-markers-timeproj-markers',
];

export const isTooltipAllowed = id =>
  typeof id === 'string' && TOOLTIP_ALLOWED_PREFIXES.some(p => id.startsWith(p));

/**
 * Placements that mean "the market has not traded this column".
 *
 * <p>EXTENDED is a projection column past the newest bar; MISSING is a date inside the charted
 * span that the axis has no column for, a weekend or a holiday. Either way there is no outcome to
 * report, so the mark must not borrow the vocabulary of one.
 */
/**
 * THE CUSTOMER'S OWN COLOUR FOR ONE ELEMENT, or the study's own default.
 *
 * <h2>Resolved at RENDER, not baked into the overlay</h2>
 *
 * An overlay carries a `colorRole` naming what it IS - a PTA vertical band, a CTS up arrow - and the
 * colour is looked up here every time the chart is built. That is what makes a saved change appear
 * immediately: a config change re-renders, and nothing has to re-run a study or walk the overlay
 * list patching hex values into objects that are already drawn.
 *
 * <p>The alternative was `updateOverlay` on save, and it is worse in a way worth recording. The CTS
 * arrows bake their colour into individual POINTS, so recolouring them means knowing which points
 * are up and which are down and which are forward markers that must stay gold - three facts the
 * overlay no longer has once it is built. A role read at render needs none of them.
 *
 * <p>The fallback is the expression the call site used before, so an unconfigured install renders
 * exactly as it did: this returns it untouched when no choice is stored.
 */
const roleColor = (options, role, fallback) =>
  (role ? resolveColor(options && options.workspaceColors, role, fallback) : fallback);

export const isPendingPlacement = placement =>
  placement === 'extended' || placement === 'missing' || placement === 'offChart';

/**
 * A MARKER'S HOVER CARD: a heading, the lines, and anything else behind a disclosure.
 *
 * <h2>⚠ ONE RENDERER, because there were two</h2>
 *
 * The arrow series and the badge series each built this HTML, and the badge's own comment said it
 * must be "the same card as the arrow" — two copies of that promise, which is how it stops being
 * true. A reader hovering the letters and hovering the triangle are asking one question about one
 * bar, and two answers would be a bug findable only by trying both.
 *
 * <h2>The disclosure</h2>
 *
 * `tooltipMoreLines` renders inside a native {@code <details>}: no state, no re-render, no
 * listener — the browser owns it. It needs {@code enterable: true} on the series so the pointer
 * can reach the summary, which is why that is opt-in per overlay rather than global.
 *
 * <p>⚠ IT IS CLOSED ON EVERY HOVER. A `<details>` is a fresh element each time the formatter runs,
 * so "open" cannot persist — stated because it looks like a bug the second time you hover.
 */
export function markerCardHtml(d, { expandable = false } = {}) {
  const data = d || {};
  const lines = Array.isArray(data.tooltipLines) ? data.tooltipLines : [];
  const more = Array.isArray(data.tooltipMoreLines) ? data.tooltipMoreLines : [];
  if (!data.name && lines.length === 0 && more.length === 0) return '';

  const MONO = 'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;'
    + 'font-size:12px;line-height:1.5;white-space:pre';

  const head = data.name
    ? `<div style="font-weight:600;margin-bottom:2px">${data.name}</div>` : '';
  // Monospace and pre, so the columns the caller padded stay columns. The Visual Universe summary
  // lays the same fields out the same way; a card that reflowed them would be a second layout.
  const body = lines.length > 0 ? `<div style="${MONO}">${lines.join('<br/>')}</div>` : '';
  const detail = expandable && more.length > 0
    ? '<details style="margin-top:4px"><summary style="cursor:pointer;opacity:0.75;'
      + 'font-size:11px;outline:none">more</summary>'
      + `<div style="${MONO};margin-top:3px">${more.join('<br/>')}</div></details>`
    : '';
  const foot = data.tooltipFooter
    ? `<div style="opacity:0.7;margin-top:2px">${data.tooltipFooter}</div>` : '';
  return head + body + detail + foot;
}

export const processChartData = (barData, chartColors, overlays, viewMode, chartType, showPatterns = false, patternVisibility, maConfigs = [], options = {}) => {
  const { markLines, markAreas, trendLines, planetaryLineSeries, tradeCards } = getMarkings(overlays, barData, options);

  // Which side each hand-drawn badge takes. Computed once from the full overlay list rather than
  // from trendLines, so the alternation follows chart order — the same numbering the journal uses,
  // which keeps "Line 1" in the summary and the first-sided badge the same line.
  const badgeSides = badgeSidesByOverlayId(overlays);

  const trendLineSeries = trendLines.map((line, index) => {
    // Always use the color set by createTrendLineOverlay; never override here
    const lineColor = line.color;

    // Measurement badge, for hand-drawn trendlines only. Study output uses this same overlay type
    // for its connector lines, and labelling those would put a "gain" on something nobody traded.
    // The `source` check is what does that work, and it is the reason the other trendline tools
    // draw without a percentage.
    //
    // showMeasure is a per-line opt-out read off the OVERLAY, absent meaning on. Nothing sets it
    // today - the toolbar toggle that used to was removed as redundant - but it is kept because it
    // is the mechanism by which a single line can be drawn without a badge, and because absent
    // meaning true is what lets it be reintroduced without touching any existing line.
    const badge = line.source === MANUAL_DRAW_SOURCE && line.showMeasure !== false
      ? formatTrendlineBadge(line.price1, line.price2)
      : '';
    // Alternating sides keep badges apart where several lines end near the same bar. Falls back to
    // the series index if an overlay somehow has no id, so the alternation never collapses to one
    // side just because a line was created without one.
    const badgeSide = badgeSides[line.id] || (index % 2 === 0 ? 'left' : 'right');

    return {
      type: 'line',
      name: `Trendline ${index + 1}`,
      data: [
        { name: 'start', value: [line.date1, line.price1] },
        { name: 'end', value: [line.date2, line.price2] },
      ],
      symbol: 'none',
      // The badge is a markPoint, NOT a label on the end data item.
      //
      // A per-datum label renders relative to that point's symbol, and this series draws none
      // (`symbol: 'none'`), so the label had nothing to attach to and never appeared. markPoint
      // carries its own symbol and label, which is why it is the pattern already used elsewhere in
      // this file for the declination and planetary line tags.
      // Composite tools (Fibonacci, pitchfork) label their own lines and choose their own dash
      // style, so `endLabel` and `lineType` are honoured here rather than each tool needing a new
      // renderer. A tool line never carries a measurement badge — it is construction geometry, not
      // a measured move — so the two label paths are mutually exclusive by construction.
      markPoint: (badge || line.endLabel) ? {
        silent: true,
        symbol: 'circle',
        // A tool line's label marks a level, not a point on it, so it gets no dot.
        symbolSize: badge ? 6 : 0,
        itemStyle: { color: lineColor },
        label: {
          show: true,
          formatter: badge || line.endLabel,
          // Measured moves alternate side to stay legible when several end near one bar. A tool's
          // own label sits at the end of the line it names; which SIDE of that point is the tool's
          // choice, because it depends on how close to the right edge the line runs. Fibonacci
          // levels ask for 'left' so a ~90px label cannot land on the price scale — see
          // fibonacciSegments. Absent means 'right', which is what every other tool had.
          position: badge ? badgeSide : (line.endLabelPosition || 'right'),
          distance: 8,
          color: badge ? '#fff' : lineColor,
          backgroundColor: badge ? lineColor : 'transparent',
          padding: [3, 6],
          borderRadius: 3,
          fontSize: 11,
          fontWeight: 'bold',
        },
        // Anchored to the second point — where the eye ends up after following the line.
        data: [{ coord: [line.date2, line.price2] }],
      } : undefined,
      lineStyle: {
        color: lineColor,
        width: line.lineWidth || 2,
        // Fibonacci levels are dotted so they read as reference levels rather than as drawn lines.
        type: line.lineType || 'solid',
        // Translucency, for a tool that draws a LADDER rather than a single line — eight opaque
        // segments across a year read as a fence and hide the price action they are about.
        //
        // ⚠ SPREAD CONDITIONALLY, so a line that asks for nothing emits no `opacity` key at all
        // and renders byte-identically to before. An unconditional `opacity: line.lineOpacity ?? 1`
        // would be equivalent to ECharts and NOT equivalent to a reader diffing the option, which
        // is how a "no-op" change comes to look like a styling change on every existing trendline.
        ...(Number.isFinite(line.lineOpacity) ? { opacity: line.lineOpacity } : {}),
      },
      z: 100, // Ensure trendlines are rendered on top
    };
  });

  // === Moving Average (MA) config ===
  // Calculate closing prices for MA, regardless of chart type
  const closingPrices = barData.ohlc.map(bar => (bar && bar[1] != null) ? bar[1] : null);
  // Calculate up to 3 MAs
  const maSeriesArr = (maConfigs || []).filter(m => m.enabled).slice(0, 3).map((ma, idx) => {
    const period = Math.max(1, Math.floor(ma.period));
    const maData = closingPrices.map((val, i, arr) => {
      if (i < period - 1) return null;
      let sum = 0;
      let hasNull = false;
      for (let j = i - period + 1; j <= i; j++) { if (arr[j] == null) { hasNull = true; break; } sum += arr[j]; }
      if (hasNull) return null;
      return sum / period;
    });
    return {
      type: 'line',
      name: `MA${idx + 1}`,
      data: maData,
      lineStyle: {
        color: ma.color,
        width: 2,
        type: 'dashed',
      },
      showSymbol: false,
      smooth: false,
      z: 90 + idx,
    };
  });
  // === End MA config ===

  let mainSeries;
  // End MA config
  if (chartType === 'candlestick') {
    mainSeries = {
      id: '__candle__',
      type: 'candlestick',
      name: 'Price',
      data: barData.ohlc,
      itemStyle: {
        color: chartColors.candlestickUp,
        color0: chartColors.candlestickDown,
        borderColor: chartColors.candlestickUp,
        borderColor0: chartColors.candlestickDown,
      },
      ...(markLines.length > 0 ? {
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: markLines,
        },
      } : {}),
      ...(markAreas.length > 0 ? {
        markArea: {
          silent: true,
          data: markAreas
        },
      } : {}),
    };
    // Add candlestick pattern markPoints if enabled
    if (showPatterns && barData && barData.ohlc && barData.ohlc.length > 0) {
      let patternMarkPoints = [];
      for (let i = 0; i < barData.ohlc.length; ++i) {
        const patterns = detectAllPatterns(barData.ohlc, i);
        // Filter patterns by visibility config
        const visiblePatterns = patterns.filter(p => patternVisibility && patternVisibility[p] !== false);
        if (visiblePatterns.length > 0) {
          patternMarkPoints.push({
            name: visiblePatterns.join(', '),
            coord: [barData.dates[i], barData.ohlc[i][1]], // dot above high
            value: '',
            symbol: 'circle',
            symbolSize: 2.4,
            itemStyle: { color: getPatternColor(visiblePatterns) },
            label: {
              show: true,
              position: 'top',
              formatter: function(params) {
                return params.name;
              },
              color: '#333',
              fontSize: 10,
              backgroundColor: 'rgba(255,255,255,0.8)',
              borderRadius: 3,
              padding: [2, 4],
              overflow: 'truncate',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              rich: {}
            },
            tooltip: { show: true, formatter: visiblePatterns.join(', ') }
          });
        }
      }
      if (patternMarkPoints.length > 0) {
        mainSeries.markPoint = {
          data: patternMarkPoints,
          symbol: 'circle',
          symbolSize: 2.4,
          label: {
            show: true,
            position: 'top',
            formatter: function(params) {
              return params.name;
            },
            color: '#333',
            fontSize: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: 3,
            padding: [2, 4],
            overflow: 'truncate',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            rich: {}
          },
          tooltip: { show: true }
        };
      }
    }
  } else if (chartType === 'hidden') {
    // The invisible view (admin only — see utils/chartViewMode.js).
    //
    // Invisible, NOT absent. Three things hang off the price series and all of them have to
    // survive:
    //
    //   markLine / markArea  every study overlay — the planetary sign bands, cycle markers,
    //                        highlighted date ranges — is attached to THIS series. Dropping the
    //                        series would take the whole study with it, which is the opposite of
    //                        what hiding the price is for.
    //   data                 the y-axis scales to the series it holds. Emptying the data would
    //                        rescale the axis, and every overlay drawn in price coordinates would
    //                        move with it.
    //   the series itself    removing it renumbers the series array and orphans the '__candle__'
    //                        id that EChartComponent patches by during study reveals.
    //
    // So: same series, same data, same markings — drawn with zero opacity. `silent` stops it
    // answering hover, so there is no tooltip or crosshair snap on a line nobody can see.
    mainSeries = {
      id: '__candle__',
      type: 'line',
      name: 'Price',
      data: closingPrices,
      lineStyle: { opacity: 0, width: 0 },
      itemStyle: { opacity: 0 },
      areaStyle: undefined,
      showSymbol: false,
      silent: true,
      smooth: false,
      ...(markLines.length > 0 ? {
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: markLines,
        },
      } : {}),
      ...(markAreas.length > 0 ? {
        markArea: {
          silent: true,
          data: markAreas
        },
      } : {}),
    };
    // No candlestick pattern markPoints here, deliberately: they label individual price bars, so
    // leaving them on would float annotations over bars that are not drawn.
  } else {
    // For line chart, use closing prices only
    mainSeries = {
      id: '__candle__',
      type: 'line',
      name: 'Price',
      data: closingPrices, // close price
      lineStyle: {
        color: chartColors.candlestickUp,
        width: 2
      },
      ...(markLines.length > 0 ? {
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: markLines,
        },
      } : {}),
      ...(markAreas.length > 0 ? {
        markArea: {
          silent: true,
          data: markAreas
        },
      } : {}),
      showSymbol: false,
      smooth: false
    };
    // Add candlestick pattern markPoints if enabled (for line chart too, above close)
    if (showPatterns && barData && barData.ohlc && barData.ohlc.length > 0) {
      let patternMarkPoints = [];
      for (let i = 0; i < barData.ohlc.length; ++i) {
        const patterns = detectAllPatterns(barData.ohlc, i);
        // Filter patterns by visibility config
        const visiblePatterns = patterns.filter(p => patternVisibility && patternVisibility[p] !== false);
        if (visiblePatterns.length > 0) {
          patternMarkPoints.push({
            name: visiblePatterns.join(', '),
            coord: [barData.dates[i], barData.ohlc[i][3]], // dot at close
            value: '',
            symbol: 'circle',
            symbolSize: 2.4,
            itemStyle: { color: getPatternColor(visiblePatterns) },
            label: {
              show: true,
              position: 'top',
              formatter: function(params) {
                return params.name;
              },
              color: '#333',
              fontSize: 10,
              backgroundColor: 'rgba(255,255,255,0.8)',
              borderRadius: 3,
              padding: [2, 4],
              overflow: 'truncate',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              rich: {}
            },
            tooltip: { show: true, formatter: visiblePatterns.join(', ') }
          });
        }
      }
      if (patternMarkPoints.length > 0) {
        mainSeries.markPoint = {
          data: patternMarkPoints,
          symbol: 'circle',
          symbolSize: 2.4,
          label: {
            show: true,
            position: 'top',
            formatter: function(params) {
              return params.name;
            },
            color: '#333',
            fontSize: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: 3,
            padding: [2, 4],
            overflow: 'truncate',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            rich: {}
          },
          tooltip: { show: true }
        };
      }
    }
  }

  const volumeSeries = {
    id: '__volume__',
    type: 'bar',
    name: 'Volume',
    data: barData.volumes.map((volume, index) => ({
      value: volume,
      itemStyle: {
        color: barData.ohlc[index][1] > barData.ohlc[index][0] ? chartColors.volumeUp : chartColors.volumeDown,
      },
    })),
    xAxisIndex: 1,
    yAxisIndex: 2, // Corrected to map to the volume y-axis
    /*
     * ITS OWN WIDTH, so no other bar series can take it.
     *
     * ECharts lays out every BAR series on one category axis as a GROUP and divides the
     * category between them. The MACD histogram is a bar series on this same axis, so switching
     * MACD on used to halve these: measured on SPY at a hundred-bar zoom, a volume bar went from
     * 21.62px to 9.67px and the volume trend became unreadable.
     *
     * `'80%'` is not a magic number — it is `1 - barCategoryGap`, ECharts' own default category
     * fill, which is to say the width a lone bar series is given. Stating it is what makes that
     * width a property of THIS series rather than of how many others happen to share the axis,
     * so the bars look the same with MACD on, off, or with whatever lands in the lower pane
     * next. A percentage rather than pixels, so it holds at every zoom.
     */
    barWidth: '80%',
  };

  // --- Utility: Scale series to volume panel ---
  function scaleSeriesToVolumePanel(dataArr, barDataVolumes) {
    const maxVolume = Math.max(...barDataVolumes, 1);
    let min = Infinity, max = -Infinity;
    for (const v of dataArr) {
      if (typeof v === 'number') {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    // Avoid divide by zero
    if (max === min) max = min + 1;
    // Scale each value to 0..maxVolume
    return dataArr.map(v => ((v - min) / (max - min)) * maxVolume);
  }

  // --- Sine Line in Volume Panel ---
  // --- Three Sine Cycles in Volume Panel ---
  const sine1Raw = barData.dates.map((date, i) => {
    const angle = (i % 360) * (Math.PI / 180);
    return Math.sin(angle);
  });
  const sine2Raw = barData.dates.map((date, i) => {
    const angle = ((i + 120) % 360) * (Math.PI / 180); // phase shift by 120°
    return Math.sin(angle);
  });
  const sine3Raw = barData.dates.map((date, i) => {
    const angle = ((i + 240) % 360) * (Math.PI / 180); // phase shift by 240°
    return Math.sin(angle);
  });
  const sine1Scaled = scaleSeriesToVolumePanel(sine1Raw, barData.volumes);
  const sine2Scaled = scaleSeriesToVolumePanel(sine2Raw, barData.volumes);
  const sine3Scaled = scaleSeriesToVolumePanel(sine3Raw, barData.volumes);
  const sine1LineData = barData.dates.map((date, i) => [date, sine1Scaled[i]]);
  const sine2LineData = barData.dates.map((date, i) => [date, sine2Scaled[i]]);
  const sine3LineData = barData.dates.map((date, i) => [date, sine3Scaled[i]]);
  const sine1LineSeries = {
    type: 'line',
    name: 'Sine Blue',
    data: sine1LineData,
    symbol: 'none',
    lineStyle: { color: 'blue', width: 2 },
    xAxisIndex: 1,
    yAxisIndex: 1,
    z: 50,
  };
  const sine2LineSeries = {
    type: 'line',
    name: 'Sine Red',
    data: sine2LineData,
    symbol: 'none',
    lineStyle: { color: 'red', width: 2 },
    xAxisIndex: 1,
    yAxisIndex: 1,
    z: 51,
  };
  const sine3LineSeries = {
    type: 'line',
    name: 'Sine Green',
    data: sine3LineData,
    symbol: 'none',
    lineStyle: { color: 'green', width: 2 },
    xAxisIndex: 1,
    yAxisIndex: 1,
    z: 52,
  };

  // Return all series, including MA, as an array for EChartComponent
  // THE TOOLTIP WHITELIST, applied once, here, to everything on its way out.
  //
  // A chokepoint rather than an edit to each of the twenty-odd series that set a tooltip. A
  // per-series audit is correct exactly until the next series is added, and the one that gets
  // forgotten is by definition the one nobody is looking at. Anything not named below leaves with
  // `tooltip: { show: false }`, so a hover card can only appear if someone put the series on the
  // list on purpose.
  if (options.restrictTooltips) {
    [trendLineSeries, planetaryLineSeries, maSeriesArr].forEach((group) => {
      if (!Array.isArray(group)) return;
      group.forEach((series) => {
        if (series && !isTooltipAllowed(series.id)) series.tooltip = { show: false };
      });
    });
  }

  return { mainSeries, maSeries: maSeriesArr, trendLineSeries, planetaryLineSeries, volumeSeries, sine1LineSeries, sine2LineSeries, sine3LineSeries, allFormattedDates: barData.dates, tradeCards };

};
